import { useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
import { confirmAsync, haptic, notifyHaptic } from "../auth";
import {
  EmptyState,
  ErrorBox,
  SkeletonRows,
  StatusPill,
} from "../components/States";
import { PeriodPicker } from "../components/PeriodPicker";
import { PullToRefresh } from "../components/PullToRefresh";
import { Icon, IconName } from "../components/Icon";
import { isTelegram, useMainButton } from "../components/telegram";
import {
  FxAgent,
  FxBalance,
  FxExchange,
  FxLot,
  FxPendingAttach,
  FxCryptoTx,
  FxPnLReport,
  FxPoolBalance,
  FxProvider,
  ExchangeTemplate,
  FxProviderLedgerEntry,
  FxPurchaseResponse,
  FxWallet,
  MeResponse,
  PaymentItem,
  PaymentsResponse,
} from "../types";

interface Props {
  me: MeResponse;
  initialSection?: Section;
}

export type Section =
  | "balances"
  | "deals"
  | "lots"
  | "agents"
  | "purchase"
  | "distribute"
  | "awaiting"
  | "pnl"
  | "report";

interface DealAllocationResult {
  deal_id: number;
  deal_currency: string;
  needed_amount: number;
  allocated_amount: number;
  lot_consumed: number;
  attached_lot_id: number | null;
  fully_covered: boolean;
  needs_conversion: boolean;
}

interface DistributeMultiResponse {
  allocations: DealAllocationResult[];
  uncovered_deal_ids: number[];
  already_covered_deal_ids: number[];
  surplus_lot: { id: number } | null;
  surplus_amount: number;
  lot_total_consumed: number;
  operation_id: number;
}

interface FxExportDeal {
  id: number;
  counterparty_id: number | null;
  counterparty_name: string | null;
  base_currency: string;
  currency: string;
  amount: number;
  status: string | null;
  deal_format: string | null;
  rate: number | null;
  rate_source: string | null;
  deferred_rate_fix: boolean;
  deal_date: string | null;
  created_at: string;
  fixed_at: string | null;
  company_name: string | null;
  bank_name: string | null;
  export_stage: number;
  fx_received_pending: boolean;
  stages: Array<{
    index: number;
    key: string;
    label: string;
    actor: string;
    done: boolean;
    is_next: boolean;
  }>;
}

interface Counterparty {
  id: number;
  name: string;
  status: string;
  kind: string;
}

// parseFloat, понимающий запятую как десятичный разделитель: рус.
// раскладка вводит «1,2», а parseFloat("1,2") вернул бы 1.
const parseDec = (s: string): number =>
  parseFloat((s || "").replace(",", "."));

export function FxPage({ me, initialSection }: Props) {
  const [section, setSection] = useState<Section>(initialSection ?? "balances");

  return (
    <div>
      <h1 className="text-xl font-semibold mb-3">💱 Экспорт</h1>

      <div className="flex flex-wrap gap-2 mb-4">
        {(
          [
            ["balances", "📊 Остатки"],
            ["deals", "📋 Заявки"],
            ["lots", "🧾 Привязанные лоты"],
            ["agents", "👥 Агенты"],
            ["purchase", "🛒 Покупка"],
            ["distribute", "📦 Распределение"],
            ["awaiting", "⏳ Ждут курс"],
            ["pnl", "📈 P&L"],
            ["report", "📥 Отчёт"],
          ] as Array<[Section, string]>
        ).map(([s, label]) => (
          <button
            key={s}
            onClick={() => setSection(s)}
            className={
              "px-3 py-1.5 rounded-full text-sm whitespace-nowrap " +
              (section === s
                ? "bg-tg-button text-tg-buttonText"
                : "bg-tg-secondaryBg text-tg-text")
            }
          >
            {label}
          </button>
        ))}
      </div>

      {section === "balances" && (
        <BalancesView isAdmin={me.roles.includes("admin")} />
      )}
      {section === "deals" && (
        <ExportDealsView canManage={me.permissions.fx_manage} />
      )}
      {section === "lots" && (
        <AttachedLotsView isAdmin={me.roles.includes("admin")} />
      )}
      {section === "agents" && <AgentsView canManage={me.permissions.fx_manage} />}
      {section === "purchase" && me.permissions.fx_manage && <PurchaseView />}
      {section === "purchase" && !me.permissions.fx_manage && (
        <Locked text="Покупки доступны админу и фин. рук." />
      )}
      {section === "distribute" && me.permissions.fx_manage && <DistributeView />}
      {section === "distribute" && !me.permissions.fx_manage && (
        <Locked text="Распределение доступно админу и фин. рук." />
      )}
      {section === "awaiting" && <AwaitingRateView />}
      {section === "pnl" && <PnLView />}
      {/* P&L-отчёт — view+export, открыт всем с fx_view (key_analytic тоже
          получит обезличенный xlsx через downloads middleware). */}
      {section === "report" && <ReportView />}
    </div>
  );
}

function Locked({ text }: { text: string }) {
  return <div className="text-tg-hint text-sm py-8 text-center">{text}</div>;
}

// ===================== Balances =====================

const SOURCE_KIND_RU: Record<string, string> = {
  purchase: "Покупка экспортной выручки",
  conversion: "Внутрибанковская конверсия",
  exchange: "Обмен через провайдера",
  split: "Часть разделённого лота",
  crypto_distribute: "Распределение крипты из пула",
};

interface BalNames {
  co: Record<number, string>;
  bk: Record<number, string>;
  cp: Record<number, string>;
}

const HOLDER_GROUPS: Array<[string, string]> = [
  ["counterparty", "На балансе экспортёров"],
  ["company_bank", "На счетах «компания × банк»"],
  ["group_pool", "Крипто-пул группы"],
  ["company", "Крипта на компаниях-плательщиках"],
];

function BalancesView({ isAdmin }: { isAdmin: boolean }) {
  const [balances, setBalances] = useState<FxBalance[] | null>(null);
  const [names, setNames] = useState<BalNames>({ co: {}, bk: {}, cp: {} });
  const [error, setError] = useState<string | null>(null);
  const [includeAttached, setIncludeAttached] = useState(true);

  const load = async () => {
    setBalances(null);
    setError(null);
    try {
      const [r, cobk, cps] = await Promise.all([
        api.get<FxBalance[]>(
          `/api/fx/balances?includeAttached=${includeAttached}`,
        ),
        loadCoBk(),
        api.get<Counterparty[]>("/api/counterparties"),
      ]);
      const cp: Record<number, string> = {};
      for (const c of cps) cp[c.id] = c.name;
      setNames({ co: cobk.coName, bk: cobk.bkName, cp });
      setBalances(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeAttached]);

  if (error) return <ErrorBox text={error} />;
  if (!balances) return <SkeletonRows />;
  if (balances.length === 0)
    return (
      <div className="text-tg-hint text-sm py-8 text-center">
        Остатков нет. Начните с раздела «Покупка».
      </div>
    );

  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 text-xs text-tg-hint">
        <input
          type="checkbox"
          checked={includeAttached}
          onChange={(e) => setIncludeAttached(e.target.checked)}
        />
        Включая привязанные к заявкам лоты
      </label>

      {HOLDER_GROUPS.map(([kind, title]) => {
        const rows = balances.filter((b) => b.holder_kind === kind);
        if (rows.length === 0) return null;
        return (
          <section key={kind}>
            <h2 className="text-sm font-medium text-tg-hint uppercase mb-2">
              {title}
            </h2>
            <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
              {rows.map((b, i) => (
                <BalanceRow
                  key={i}
                  b={b}
                  names={names}
                  includeAttached={includeAttached}
                  isAdmin={isAdmin}
                  onChanged={load}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function holderTitle(b: FxBalance, names: BalNames): string {
  if (b.holder_kind === "counterparty")
    return names.cp[b.holder_id_counterparty ?? -1] || "Контрагент";
  if (b.holder_kind === "company_bank")
    return (
      (names.co[b.holder_id_company ?? -1] || "Компания") +
      " · " +
      (names.bk[b.holder_id_bank ?? -1] || "банк")
    );
  if (b.holder_kind === "group_pool") return "Крипто-пул группы";
  if (b.holder_kind === "company")
    return names.co[b.holder_id_company ?? -1] || "Компания";
  return b.holder_kind;
}

interface ImportCandidate {
  deal_id: number;
  counterparty_name: string | null;
  amount: number;
  currency: string;
  covered: number;
  remaining_need: number;
}

function BalanceRow({
  b,
  names,
  includeAttached,
  isAdmin,
  onChanged,
}: {
  b: FxBalance;
  names: BalNames;
  includeAttached: boolean;
  isAdmin: boolean;
  onChanged?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [lots, setLots] = useState<FxLot[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  // Опциональная ручная привязка свободного лота к импортной заявке.
  // База — FIFO при оплате; это override для конкретного лота.
  const [pickFor, setPickFor] = useState<number | null>(null);
  const [cands, setCands] = useState<ImportCandidate[] | null>(null);

  const num = (n: number) =>
    n.toLocaleString("ru-RU", { maximumFractionDigits: 4 });

  const openPick = async (lotId: number) => {
    setError(null);
    setPickFor(lotId);
    setCands(null);
    try {
      setCands(
        await api.get<ImportCandidate[]>(
          `/api/fx/lots/${lotId}/import_candidates`,
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPickFor(null);
    }
  };

  const attachTo = async (lotId: number, dealId: number) => {
    setBusy(lotId);
    setError(null);
    try {
      await api.post(`/api/fx/deals/${dealId}/attach-lot`, { lotId });
      setPickFor(null);
      setCands(null);
      await loadLots();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const loadLots = async () => {
    const qs = new URLSearchParams();
    qs.set("holderKind", b.holder_kind);
    qs.set("currency", b.currency);
    qs.set("onlyWithRemainder", "true");
    if (b.holder_id_company != null)
      qs.set("companyId", String(b.holder_id_company));
    if (b.holder_id_bank != null) qs.set("bankId", String(b.holder_id_bank));
    if (b.holder_id_counterparty != null)
      qs.set("counterpartyId", String(b.holder_id_counterparty));
    const r = await api.get<FxLot[]>(`/api/fx/lots?${qs.toString()}`);
    setLots(includeAttached ? r : r.filter((l) => l.attached_deal_id == null));
  };

  const detach = async (lotId: number) => {
    setBusy(lotId);
    setError(null);
    try {
      await api.post(`/api/fx/lots/${lotId}/detach`, {});
      await loadLots();
      onChanged?.(); // перечитать агрегат остатков (свободные/привязанные)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && lots === null) {
      try {
        const qs = new URLSearchParams();
        qs.set("holderKind", b.holder_kind);
        qs.set("currency", b.currency);
        qs.set("onlyWithRemainder", "true");
        if (b.holder_id_company != null)
          qs.set("companyId", String(b.holder_id_company));
        if (b.holder_id_bank != null)
          qs.set("bankId", String(b.holder_id_bank));
        if (b.holder_id_counterparty != null)
          qs.set("counterpartyId", String(b.holder_id_counterparty));
        const r = await api.get<FxLot[]>(`/api/fx/lots?${qs.toString()}`);
        setLots(
          includeAttached ? r : r.filter((l) => l.attached_deal_id == null),
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    }
  };

  return (
    <div onClick={toggle} className="px-4 py-3 text-sm cursor-pointer">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium break-words">
            {holderTitle(b, names)}
          </div>
          <div className="text-xs text-tg-hint">
            средн. себестоимость {b.average_rate.toFixed(4)} ₽/{b.currency}
          </div>
        </div>
        <div className="font-semibold tabular-nums whitespace-nowrap">
          {b.total_amount.toLocaleString("ru-RU", {
            maximumFractionDigits: 4,
          })}{" "}
          <span className="text-tg-hint">{b.currency}</span>
        </div>
      </div>

      {open && (
        <div className="mt-2 pt-2 border-t border-tg-secondaryBg">
          <div className="text-[11px] uppercase tracking-wider text-tg-hint mb-1.5">
            Состав остатка — лоты и маршрут поступления
          </div>
          {error && <div className="text-xs text-red-700">{error}</div>}
          {lots === null && !error && (
            <div className="text-xs text-tg-hint">Загрузка…</div>
          )}
          {lots !== null && lots.length === 0 && (
            <div className="text-xs text-tg-hint">Лотов нет.</div>
          )}
          {lots !== null && lots.length > 0 && (
            <div className="space-y-1.5">
              {lots.map((l) => (
                <div
                  key={l.id}
                  className="text-xs bg-tg-secondaryBg rounded-lg p-2"
                >
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">
                      {SOURCE_KIND_RU[l.source_kind] || l.source_kind}
                    </span>
                    <span className="tabular-nums">
                      {l.remaining_amount.toLocaleString("ru-RU", {
                        maximumFractionDigits: 4,
                      })}{" "}
                      {l.currency}
                    </span>
                  </div>
                  <div className="text-tg-hint mt-0.5">
                    Лот #{l.id} · себестоимость{" "}
                    {l.rate_to_rub.toFixed(4)} ₽/{l.currency} · от{" "}
                    {fmtDT(l.created_at)}
                    {l.attached_deal_id != null
                      ? ` · привязан к заявке №${l.attached_deal_id}`
                      : ""}
                  </div>
                  {isAdmin &&
                    l.attached_deal_id != null &&
                    l.holder_kind === "company_bank" && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          void detach(l.id);
                        }}
                        disabled={busy === l.id}
                        className="mt-1 px-2 py-1 rounded-md bg-amber-600 text-white text-[11px] disabled:opacity-50"
                      >
                        {busy === l.id
                          ? "Отвязываю…"
                          : "🔓 Отвязать лот (вернуть в общий пул)"}
                      </button>
                    )}
                  {isAdmin &&
                    l.attached_deal_id == null &&
                    l.holder_kind === "company_bank" &&
                    (pickFor === l.id ? (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 bg-tg-bg rounded-md p-2"
                      >
                        <div className="text-[11px] text-tg-hint mb-1">
                          Импортные заявки на этой паре в {l.currency}. По
                          умолчанию валюта спишется FIFO при оплате — ручная
                          привязка нужна, только если хотите закрепить именно
                          этот лот:
                        </div>
                        {cands === null ? (
                          <div className="text-[11px] text-tg-hint">Загрузка…</div>
                        ) : cands.length === 0 ? (
                          <div className="text-[11px] text-tg-hint">
                            Нет подходящих импортных заявок.
                          </div>
                        ) : (
                          <div className="space-y-1">
                            {cands.map((c) => (
                              <button
                                key={c.deal_id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void attachTo(l.id, c.deal_id);
                                }}
                                disabled={busy === l.id}
                                className="w-full text-left text-[11px] bg-tg-secondaryBg rounded-md p-1.5 disabled:opacity-50"
                              >
                                Заявка №{c.deal_id}
                                {c.counterparty_name
                                  ? ` · ${c.counterparty_name}`
                                  : ""}{" "}
                                · нужно {num(c.remaining_need)} {c.currency}
                                {c.covered > 0
                                  ? ` (привязано ${num(c.covered)})`
                                  : ""}
                              </button>
                            ))}
                          </div>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPickFor(null);
                            setCands(null);
                          }}
                          className="mt-1 text-[11px] text-tg-hint"
                        >
                          Отмена
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          void openPick(l.id);
                        }}
                        disabled={busy === l.id}
                        className="mt-1 px-2 py-1 rounded-md bg-tg-secondaryBg text-tg-text text-[11px] disabled:opacity-50"
                      >
                        🔗 Привязать к импортной заявке вручную (опц.)
                      </button>
                    ))}
                  <div className="text-tg-hint mt-0.5">
                    Закуплено по курсу {l.rate_to_rub.toFixed(4)} ₽ ·
                    рублёвая стоимость лота ≈{" "}
                    {(l.remaining_amount * l.rate_to_rub).toLocaleString(
                      "ru-RU",
                      { maximumFractionDigits: 2 },
                    )}{" "}
                    ₽
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="text-xs text-tg-link mt-1">
        {open ? "▲ свернуть" : "▼ детали остатка"}
      </div>
    </div>
  );
}

// ===================== Привязанные лоты (плоский список) =====================

function AttachedLotsView({ isAdmin }: { isAdmin: boolean }) {
  const [lots, setLots] = useState<FxLot[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  // По умолчанию показываем ВСЕ привязанные лоты (в т.ч. с остатком 0),
  // т.к. удаление заявки блокируется по любому привязанному лоту.
  const [onlyRemainder, setOnlyRemainder] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const r = await api.get<FxLot[]>(
        `/api/fx/lots?attachedOnly=true&onlyWithRemainder=${onlyRemainder}&limit=2000`,
      );
      setLots(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyRemainder]);

  const detach = async (lotId: number) => {
    setBusy(lotId);
    setError(null);
    try {
      await api.post(`/api/fx/lots/${lotId}/detach`, {});
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="text-xs text-tg-hint mb-2">
        Все лоты, привязанные к заявкам (по всем компаниям/банкам). Здесь
        видно каждую заявку с распределённым лотом и можно отвязать лот
        (валюта вернётся в общий пул).
      </div>
      <label className="flex items-center gap-2 text-xs text-tg-hint mb-3">
        <input
          type="checkbox"
          checked={onlyRemainder}
          onChange={(e) => setOnlyRemainder(e.target.checked)}
        />
        только лоты с остатком &gt; 0 (скрыть полностью списанные)
      </label>

      {error && <div className="text-xs text-red-700 mb-2">{error}</div>}
      {lots === null && !error && (
        <div className="text-tg-hint text-sm">Загрузка…</div>
      )}
      {lots !== null && lots.length === 0 && (
        <div className="text-tg-hint text-sm">Привязанных лотов нет.</div>
      )}

      <div className="space-y-2">
        {lots?.map((l) => (
          <div key={l.id} className="bg-tg-sectionBg rounded-xl p-3 text-sm">
            <div className="flex justify-between gap-2">
              <span className="font-medium">
                Заявка №{l.attached_deal_id}
              </span>
              <span className="tabular-nums">
                {l.remaining_amount.toLocaleString("ru-RU", {
                  maximumFractionDigits: 4,
                })}{" "}
                / {l.initial_amount.toLocaleString("ru-RU", {
                  maximumFractionDigits: 4,
                })}{" "}
                {l.currency}
              </span>
            </div>
            <div className="text-xs text-tg-hint mt-0.5">
              Лот #{l.id} · {SOURCE_KIND_RU[l.source_kind] || l.source_kind} ·
              себестоимость {l.rate_to_rub.toFixed(4)} ₽/{l.currency} · от{" "}
              {fmtDT(l.created_at)}
            </div>
            {isAdmin && l.holder_kind === "company_bank" && (
              <button
                onClick={() => void detach(l.id)}
                disabled={busy === l.id}
                className="mt-2 px-2.5 py-1 rounded-md bg-amber-600 text-white text-xs disabled:opacity-50"
              >
                {busy === l.id
                  ? "Отвязываю…"
                  : "🔓 Отвязать лот (вернуть в общий пул)"}
              </button>
            )}
            {isAdmin && l.holder_kind !== "company_bank" && (
              <div className="mt-2 text-[11px] text-tg-hint">
                Отвязать нельзя (лот не на «компания × банк»). Заявку можно
                удалить только вместе с FX-данными (force).
              </div>
            )}
            {l.remaining_amount === 0 && (
              <div className="mt-1 text-[11px] text-tg-hint">
                Лот полностью списан (остаток 0) — отвязка снимет привязку,
                но валюты в пул не вернёт.
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ===================== Export deals =====================

const DEAL_STATUS_RU: Record<string, string> = {
  new: "Новая",
  scheduled: "Запланирована",
  fixed_prepay: "Зафиксирована",
  fixed_awaiting: "Зафикс., ждёт рубли",
  awaiting_funds: "Ожидаем валюту",
  in_day_plan: "В плане дня",
  export_fx_sent: "Валюта направлена",
  confirmed: "Закрыта",
  reopened: "Переоткрыта",
  postponed: "Перенесена",
  cancelled: "Отменена",
  deferred_rate: "Курс отложен",
  awaiting_rate_fix: "Ждёт фиксации курса",
};

// Семантический тон статуса экспортной заявки для StatusPill.
function dealStatusTone(
  status: string | null | undefined,
): "neutral" | "info" | "success" | "warning" | "danger" {
  if (!status) return "neutral";
  if (status === "confirmed") return "success";
  if (status === "cancelled") return "danger";
  if (status === "export_fx_sent") return "info";
  if (
    [
      "fixed_prepay",
      "fixed_awaiting",
      "awaiting_funds",
      "reopened",
      "postponed",
      "deferred_rate",
      "awaiting_rate_fix",
    ].includes(status)
  )
    return "warning";
  return "neutral";
}

const RATE_SOURCE_RU: Record<string, string> = {
  cbr: "ЦБ",
  cbr_plus: "ЦБ + %",
  cbr_minus: "ЦБ − %",
  special: "Спец-курс",
  manual: "Ручной",
};

const DEAL_FORMAT_RU: Record<string, string> = {
  prepay: "Предоплата",
  against_pp: "Против ПП",
  letter_of_credit: "Аккредитив",
  payment_after_receipt: "Оплата после получения валюты",
};

// Дата (YYYY-MM-DD) или дата-время ISO → короткий русский формат.
function fmtDT(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    ...(dateOnly ? {} : { hour: "2-digit", minute: "2-digit" }),
  });
}

// Подробная карточка заявки — раскрывается по клику, как в реестре
// платежей. rows — пары [подпись, значение].
function DealDetailRows({ rows }: { rows: Array<[string, string]> }) {
  if (rows.length === 0) return null;
  return (
    <div className="mt-2 pt-2 border-t border-tg-secondaryBg">
      <div className="text-[11px] uppercase tracking-wider text-tg-hint mb-1.5">
        Детали заявки
      </div>
      <dl className="text-xs grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-tg-hint">{k}</dt>
            <dd className="text-tg-text break-words">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function exportDealRows(d: FxExportDeal): Array<[string, string]> {
  const rows: Array<[string, string]> = [];
  rows.push(["Контрагент-экспортёр", d.counterparty_name || "—"]);
  rows.push([
    "Поступление",
    `${d.amount.toLocaleString("ru-RU")} ${d.base_currency}`,
  ]);
  rows.push(["Направляем экспортёру", d.currency]);
  if (d.deal_format)
    rows.push(["Формат", DEAL_FORMAT_RU[d.deal_format] || d.deal_format]);
  if (d.company_name) rows.push(["Компания", d.company_name]);
  if (d.bank_name) rows.push(["Банк", d.bank_name]);
  if (d.status)
    rows.push(["Статус", DEAL_STATUS_RU[d.status] || d.status]);
  rows.push([
    "Курс фиксации",
    d.rate != null
      ? `${d.rate.toFixed(4)}${
          d.rate_source
            ? ` (${RATE_SOURCE_RU[d.rate_source] || d.rate_source})`
            : ""
        }`
      : "не зафиксирован",
  ]);
  if (d.deferred_rate_fix) rows.push(["Отложенный фикс курса", "да"]);
  if (d.deal_date) rows.push(["Создана", fmtDT(d.deal_date)]);
  if (d.fixed_at) rows.push(["Зафиксирована", fmtDT(d.fixed_at)]);
  return rows;
}

interface DealSource {
  deal_id: number;
  currency: string;
  amount: number;
  // Поля могут отсутствовать, если backend ещё на старой версии ответа
  // (рассинхрон деплоя). Панель обязана это пережить, а не падать в белый
  // экран — поэтому всё помечено optional и читается через guard'ы.
  revenue?: {
    fixed: boolean;
    currency: string;
    amount: number;
    rate: number | null;
    rub: number | null;
  };
  attached_sum?: number;
  attached?: FxLot[];
  available?: FxLot[];
}

// Заяв-центричная панель «Валютная выручка по заявке» (только админ/финрук).
// Экспортная заявка — источник валюты: после фиксации курса оприходуется
// валютным лотом в валюте выручки (base_currency). Привязка свободного лота
// с пары «компания × банк» сохранена как опция.
function DealSourcePanel({ dealId }: { dealId: number }) {
  const [src, setSrc] = useState<DealSource | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      setSrc(await api.get<DealSource>(`/api/fx/deals/${dealId}/source`));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  if (!src) return null;
  const rev = src.revenue;
  const currency = src.currency ?? "";
  const num = (n: number | null | undefined) =>
    n == null ? "—" : Number(n).toLocaleString("ru-RU");

  return (
    <div className="mt-2 pt-2 border-t border-tg-secondaryBg">
      <div className="text-[11px] uppercase tracking-wider text-tg-hint mb-1.5">
        Валютная выручка по заявке
      </div>
      {error && <div className="text-xs text-red-700 mb-1">{error}</div>}

      {rev &&
        (rev.fixed ? (
          <div className="text-xs mb-2">
            Оприходовано:{" "}
            <span className="tabular-nums font-medium text-emerald-600">
              {num(rev.amount)} {rev.currency ?? currency}
            </span>{" "}
            @ {rev.rate != null ? Number(rev.rate).toFixed(4) : "—"}
            {rev.rub != null && (
              <span className="text-tg-hint">
                {" "}
                (= {num(rev.rub)} ₽)
              </span>
            )}
          </div>
        ) : (
          <div className="text-xs mb-2 text-amber-700">
            Курс не зафиксирован — выручка ещё не оприходована (отложенный фикс).
          </div>
        ))}

      {/* Экспортная заявка — ИСТОЧНИК валюты, к ней лоты не привязываются.
          Распределение выручки на «компания × банк» — во вкладке
          «Распределение»; привязка остатков к импортным заявкам —
          там же / во вкладке «Ждут курс». */}
      <div className="text-[11px] text-tg-hint">
        Распределение на «компания × банк» — во вкладке «Распределение».
      </div>
    </div>
  );
}

// Фиксация курса экспортной заявки прямо в мини-аппе (админ/финрук):
// фиксирует курс и оприходует валютную выручку лотом (→ «Остатки»).
function FixRateForm({
  deal,
  onDone,
}: {
  deal: FxExportDeal;
  onDone: () => void;
}) {
  const currency = deal.base_currency;
  const [agents, setAgents] = useState<FxAgent[]>([]);
  const [rateSource, setRateSource] = useState("cbr");
  const [rateModifier, setRateModifier] = useState("");
  const [cbrRate, setCbrRate] = useState<number | null>(null);
  const [cbrLoading, setCbrLoading] = useState(false);
  const [manualRate, setManualRate] = useState("");
  const [agentId, setAgentId] = useState<number | null>(null);
  const [applyDefault, setApplyDefault] = useState(true);
  const [commissionKind, setCommissionKind] = useState<"pct" | "fixed_rub">(
    "pct",
  );
  const [commissionValue, setCommissionValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<FxAgent[]>("/api/fx/agents?activeOnly=true")
      .then(setAgents)
      .catch(() => setAgents([]));
  }, []);

  useEffect(() => {
    if (rateSource === "special") return;
    setCbrLoading(true);
    setCbrRate(null);
    api
      .get<{ rate: number | null }>(
        `/api/fx/cbr_rate?currency=${encodeURIComponent(currency)}`,
      )
      .then((r) => setCbrRate(r.rate))
      .catch(() => setCbrRate(null))
      .finally(() => setCbrLoading(false));
  }, [currency, rateSource]);

  const finalRate: number | null = (() => {
    if (rateSource === "special") {
      const v = parseDec(manualRate);
      return isNaN(v) || v <= 0 ? null : v;
    }
    if (cbrRate == null) return null;
    if (rateSource === "cbr") return cbrRate;
    const m = parseDec(rateModifier);
    if (isNaN(m) || m < 0) return null;
    return rateSource === "cbr_plus"
      ? cbrRate * (1 + m / 100)
      : cbrRate * (1 - m / 100);
  })();

  const selectedAgent = agents.find((a) => a.id === agentId);
  useEffect(() => {
    if (!selectedAgent || !applyDefault) return;
    if (selectedAgent.default_pct != null) {
      setCommissionKind("pct");
      setCommissionValue(String(selectedAgent.default_pct));
    } else if (selectedAgent.default_fixed_rub != null) {
      setCommissionKind("fixed_rub");
      setCommissionValue(String(selectedAgent.default_fixed_rub));
    }
  }, [agentId, applyDefault]); // eslint-disable-line

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    haptic("light");
    try {
      if (finalRate == null) throw new Error("Курс не определён");
      const body: Record<string, unknown> = {
        rate_to_rub: finalRate,
        rate_source: rateSource,
      };
      if (rateSource === "cbr_plus" || rateSource === "cbr_minus")
        body.rate_modifier_pct = parseDec(rateModifier);
      if (agentId) body.agent_id = agentId;
      if (commissionValue && agentId) {
        body.commission_kind = commissionKind;
        body.commission_value = parseDec(commissionValue);
      }
      await api.post(`/api/fx/export_deals/${deal.id}/fix_rate`, body);
      notifyHaptic("success");
      onDone();
    } catch (e) {
      notifyHaptic("error");
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-2 bg-tg-secondaryBg rounded-lg p-2 space-y-2">
      <div className="text-[11px] uppercase tracking-wider text-tg-hint">
        Фиксация курса · выручка {deal.amount.toLocaleString("ru-RU")} {currency}
      </div>

      <Field label="Источник курса">
        <select
          value={rateSource}
          onChange={(e) => setRateSource(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-tg-bg text-sm"
        >
          <option value="cbr">📈 Курс ЦБ</option>
          <option value="cbr_plus">➕ ЦБ + X %</option>
          <option value="cbr_minus">➖ ЦБ − X %</option>
          <option value="special">✏️ Свой курс (спец)</option>
        </select>
      </Field>

      {rateSource !== "special" && (
        <div className="text-xs text-tg-hint -mt-1">
          {cbrLoading
            ? "Загружаем курс ЦБ…"
            : cbrRate != null
            ? `Курс ЦБ на сегодня: ${cbrRate.toFixed(4)} ₽/${currency}`
            : "⚠️ Курс ЦБ недоступен — выберите «Свой курс»"}
        </div>
      )}

      {(rateSource === "cbr_plus" || rateSource === "cbr_minus") && (
        <Field label="Модификатор, %">
          <input
            value={rateModifier}
            onChange={(e) => setRateModifier(e.target.value)}
            inputMode="decimal"
            placeholder="0.5"
            className="w-full px-3 py-2 rounded-lg bg-tg-bg text-sm"
          />
        </Field>
      )}

      {rateSource === "special" && (
        <Field label="Курс (RUB за 1 единицу валюты)">
          <input
            value={manualRate}
            onChange={(e) => setManualRate(e.target.value)}
            inputMode="decimal"
            placeholder="92.5"
            className="w-full px-3 py-2 rounded-lg bg-tg-bg text-sm"
          />
        </Field>
      )}

      {finalRate != null && (
        <div className="px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-900">
          💱 Итоговый курс:{" "}
          <span className="font-semibold tabular-nums">
            {finalRate.toFixed(6)} ₽/{currency}
          </span>
        </div>
      )}

      <Field label="Агент-посредник — комиссия (опц.)">
        <select
          value={agentId ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            setAgentId(v ? parseInt(v, 10) : null);
            setApplyDefault(true);
          }}
          className="w-full px-3 py-2 rounded-lg bg-tg-bg text-sm"
        >
          <option value="">— без агента —</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </Field>

      {agentId && (
        <div className="grid grid-cols-2 gap-2">
          <select
            value={commissionKind}
            onChange={(e) => {
              setCommissionKind(e.target.value as "pct" | "fixed_rub");
              setApplyDefault(false);
            }}
            className="px-3 py-2 rounded-lg bg-tg-bg text-sm"
          >
            <option value="pct">% от объёма</option>
            <option value="fixed_rub">Фикс. RUB</option>
          </select>
          <input
            value={commissionValue}
            onChange={(e) => {
              setCommissionValue(e.target.value);
              setApplyDefault(false);
            }}
            inputMode="decimal"
            placeholder={commissionKind === "pct" ? "%" : "RUB"}
            className="px-3 py-2 rounded-lg bg-tg-bg text-sm"
          />
        </div>
      )}

      {error && (
        <div className="p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">
          {error}
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => void submit()}
          disabled={submitting || finalRate == null}
          className="px-3 py-1.5 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
        >
          {submitting ? "Фиксирую…" : "Зафиксировать и оприходовать"}
        </button>
        <button
          onClick={onDone}
          className="px-3 py-1.5 rounded-lg bg-tg-bg text-tg-text text-sm"
        >
          Отмена
        </button>
      </div>
    </div>
  );
}

function ExportDealsView({ canManage }: { canManage: boolean }) {
  const [deals, setDeals] = useState<FxExportDeal[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [busyId, setBusyId] = useState<number | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [fixingId, setFixingId] = useState<number | null>(null);

  const advanceStage = async (id: number, expected: number) => {
    setBusyId(id);
    setError(null);
    setNote(null);
    try {
      const r = await api.post<{
        confirmed_label: string;
        confirmed_key: string;
        rate_fixed: boolean;
        all_done: boolean;
      }>(`/api/fx/export_deals/${id}/advance_stage`, {
        expected_stage: expected,
      });
      let m = `Заявка №${id}: этап «${r.confirmed_label}» отмечен.`;
      if (r.confirmed_key === "fx_received" && !r.rate_fixed)
        m += " Теперь зафиксируйте курс, чтобы выручка попала в «Остатки».";
      setNote(m);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const load = async () => {
    setDeals(null);
    setError(null);
    try {
      const r = await api.get<FxExportDeal[]>("/api/fx/export_deals");
      setDeals(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (error) return <ErrorBox text={error} />;
  if (!deals) return <SkeletonRows />;
  if (deals.length === 0)
    return (
      <div className="text-tg-hint text-sm py-8 text-center">
        Экспортных заявок пока нет. Они создаются через бота в личных
        сообщениях (тип сделки «Экспорт»).
      </div>
    );

  return (
    <>
    {note && (
      <div className="mb-3 rounded-lg border border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 p-2.5 text-xs text-emerald-900 dark:text-emerald-200">
        {note}
      </div>
    )}
    <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
      {deals.map((d) => {
        const isOpen = expanded.has(d.id);
        return (
          <div
            key={d.id}
            onClick={() => toggle(d.id)}
            className="px-4 py-3 text-sm cursor-pointer"
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-medium">Заявка №{d.id}</span>
              <span className="text-xs text-tg-hint">
                {d.deal_date || ""}
              </span>
            </div>
            <div className="mt-0.5">
              Поступление:{" "}
              <span className="font-semibold tabular-nums">
                {d.amount.toLocaleString("ru-RU")} {d.base_currency}
              </span>
              <span className="text-tg-hint"> → экспортёру {d.currency}</span>
            </div>
            <div className="text-xs text-tg-hint mt-0.5">
              {d.counterparty_name || "контрагент —"}
              {d.company_name ? ` · ${d.company_name}` : ""}
              {d.bank_name ? ` · ${d.bank_name}` : ""}
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <StatusPill tone={dealStatusTone(d.status)}>
                {d.status ? DEAL_STATUS_RU[d.status] || d.status : "—"}
              </StatusPill>
              {d.rate != null ? (
                <span className="text-xs text-tg-hint">
                  курс {d.rate.toFixed(4)}
                  {d.rate_source
                    ? ` (${RATE_SOURCE_RU[d.rate_source] || d.rate_source})`
                    : ""}
                </span>
              ) : (
                <span className="text-xs text-amber-700">
                  курс не зафиксирован
                </span>
              )}
              {d.deferred_rate_fix && d.rate == null && (
                <span className="text-xs text-amber-700">
                  · отложенный фикс
                </span>
              )}
              {canManage && d.rate == null && fixingId !== d.id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setNote(null);
                    setFixingId(d.id);
                  }}
                  className="px-2 py-0.5 rounded-md bg-tg-button text-tg-buttonText text-[11px]"
                >
                  💱 Зафиксировать курс
                </button>
              )}
            </div>
            {fixingId === d.id && (
              <div onClick={(e) => e.stopPropagation()}>
                <FixRateForm
                  deal={d}
                  onDone={() => {
                    setFixingId(null);
                    setNote(`Заявка №${d.id}: курс зафиксирован, выручка оприходована — см. «Остатки».`);
                    void load();
                  }}
                />
              </div>
            )}
            {d.stages.length > 0 && (
              <div className="mt-1.5 space-y-1">
                <div className="text-[11px] uppercase tracking-wider text-tg-hint">
                  Этапы исполнения
                </div>
                {d.stages.map((s) => (
                  <div
                    key={s.key}
                    className="flex items-center justify-between gap-2"
                  >
                    <span
                      className={
                        "text-xs " +
                        (s.done ? "text-emerald-600" : "text-tg-hint")
                      }
                    >
                      {s.done ? "✅" : "▢"} {s.label}
                      {s.key === "fx_received" ? " 💱" : ""}
                    </span>
                    {canManage && s.is_next && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          void advanceStage(d.id, d.export_stage);
                        }}
                        disabled={busyId === d.id}
                        className="shrink-0 px-2 py-0.5 rounded-md bg-emerald-600 text-white text-[11px] disabled:opacity-50"
                      >
                        {busyId === d.id ? "…" : "Отметить выполненным"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {isOpen && <DealDetailRows rows={exportDealRows(d)} />}
            {isOpen && canManage && (
              <div onClick={(e) => e.stopPropagation()}>
                <DealSourcePanel dealId={d.id} />
              </div>
            )}
            <div className="text-xs text-tg-link mt-1.5">
              {isOpen ? "▲ свернуть" : "▼ подробнее"}
            </div>
          </div>
        );
      })}
    </div>
    </>
  );
}

// ===================== Agents =====================

function AgentsView({ canManage }: { canManage: boolean }) {
  const [agents, setAgents] = useState<FxAgent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [defaultPct, setDefaultPct] = useState("");
  const [defaultFixed, setDefaultFixed] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setAgents(null);
    try {
      const r = await api.get<FxAgent[]>("/api/fx/agents?activeOnly=true");
      setAgents(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setEditId(null);
    setName("");
    setDefaultPct("");
    setDefaultFixed("");
    setShowForm(false);
  };

  const startCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const startEdit = (a: FxAgent) => {
    setEditId(a.id);
    setName(a.name);
    setDefaultPct(a.default_pct != null ? String(a.default_pct) : "");
    setDefaultFixed(
      a.default_fixed_rub != null ? String(a.default_fixed_rub) : "",
    );
    setShowForm(true);
  };

  // Создание и редактирование используют одну форму: editId решает,
  // POST это или PUT. Пустые поля дефолтов сбрасывают ставку в NULL.
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { name };
      body.default_pct = defaultPct ? parseDec(defaultPct) : null;
      body.default_fixed_rub = defaultFixed ? parseDec(defaultFixed) : null;
      if (editId != null) {
        await api.put<FxAgent>(`/api/fx/agents/${editId}`, body);
      } else {
        await api.post<FxAgent>("/api/fx/agents", body);
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id: number) => {
    try {
      await fetch(`/api/fx/agents/${id}`, {
        method: "DELETE",
        headers: {
          "X-Telegram-Init-Data":
            (window as any).Telegram?.WebApp?.initData || "",
        },
      });
      await load();
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-2.5 rounded-lg bg-tg-sectionBg text-xs text-tg-hint leading-relaxed">
        <b className="text-tg-text">Агент</b> — посредник, которому платится
        комиссия за покупку валюты (Fix / Turnover fee). Это <b>не</b> продавец
        валюты: продавец — это контрагент-экспортёр, он выбирается отдельно
        на вкладке «Покупка». Агент не подменяет контрагента.
      </div>

      {error && (
        <div className="p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">
          {error}
        </div>
      )}

      {canManage && !showForm && (
        <button
          onClick={startCreate}
          className="w-full px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm"
        >
          ➕ Добавить агента
        </button>
      )}

      {canManage && showForm && (
        <form onSubmit={submit} className="bg-tg-sectionBg rounded-xl p-3 space-y-2">
          <div className="text-xs font-medium text-tg-text">
            {editId != null ? "Редактирование агента" : "Новый агент"}
          </div>
          <input
            placeholder="Имя агента"
            value={name}
            onChange={(e) => setName(e.target.value)}
            enterKeyHint="next"
            className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
          />
          <input
            placeholder="Дефолтный % (опц.)"
            value={defaultPct}
            onChange={(e) => setDefaultPct(e.target.value)}
            enterKeyHint="next"
            inputMode="decimal"
            className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
          />
          <input
            placeholder="Дефолт фикс RUB (опц.)"
            value={defaultFixed}
            onChange={(e) => setDefaultFixed(e.target.value)}
            enterKeyHint="done"
            inputMode="decimal"
            className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="flex-1 px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
            >
              {editId != null ? "Сохранить изменения" : "Сохранить"}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
            >
              Отмена
            </button>
          </div>
        </form>
      )}

      {agents === null ? (
        <SkeletonRows />
      ) : agents.length === 0 ? (
        <div className="text-tg-hint text-sm py-4 text-center">
          Агентов пока нет.
        </div>
      ) : (
        <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
          {agents.map((a) => (
            <div
              key={a.id}
              className="px-4 py-3 flex items-baseline justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="font-medium text-sm">{a.name}</div>
                <div className="text-xs text-tg-hint">
                  {a.default_pct != null && (
                    <>дефолт {Number(a.default_pct).toFixed(4)}% · </>
                  )}
                  {a.default_fixed_rub != null && (
                    <>дефолт {a.default_fixed_rub} ₽ · </>
                  )}
                  ID #{a.id}
                </div>
              </div>
              {canManage && (
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <button
                    onClick={() => startEdit(a)}
                    className="text-tg-link text-xs"
                  >
                    Изменить
                  </button>
                  <button
                    onClick={() => deactivate(a.id)}
                    className="text-tg-destructive text-xs"
                  >
                    Деактивировать
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===================== Counterparty selector =====================

function CounterpartySelector({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  const [list, setList] = useState<Counterparty[]>([]);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [filter, setFilter] = useState("");
  const [error, setError] = useState<string | null>(null);

  // FX-покупка: показываем только контрагентов-экспортёров (export+both).
  const load = async () => {
    try {
      const r = await api.get<Counterparty[]>(
        "/api/counterparties?kind=export",
      );
      setList(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const r = await api.post<Counterparty>("/api/counterparties", {
        name: newName,
        kind: "export",
      });
      setNewName("");
      setAdding(false);
      await load();
      onChange(r.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (adding) {
    return (
      <form onSubmit={add} className="flex gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Имя контрагента-экспортёра"
          enterKeyHint="done"
          autoFocus
          className="flex-1 px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
        />
        <button
          type="submit"
          disabled={!newName.trim()}
          className="px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
        >
          ✓
        </button>
        <button
          type="button"
          onClick={() => setAdding(false)}
          className="px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
        >
          ✕
        </button>
      </form>
    );
  }

  const fNorm = filter.trim().toLowerCase();
  // Дедуп по id (на случай повторов из API) + фильтр по подстроке.
  const seen = new Set<number>();
  const uniqueList = list.filter((c) => {
    if (seen.has(c.id)) return false;
    seen.add(c.id);
    return true;
  });
  const filtered = (
    fNorm
      ? uniqueList.filter((c) => c.name.toLowerCase().includes(fNorm))
      : uniqueList
  )
    .slice()
    .sort((a, b) => {
      // Релевантность: сначала те, чьё название начинается с запроса,
      // затем — алфавит. Иначе искомое тонет в середине списка.
      if (fNorm) {
        const ap = a.name.toLowerCase().startsWith(fNorm) ? 0 : 1;
        const bp = b.name.toLowerCase().startsWith(fNorm) ? 0 : 1;
        if (ap !== bp) return ap - bp;
      }
      return a.name.localeCompare(b.name, "ru");
    });
  // Гарантируем, что выбранный контрагент всегда в списке опций,
  // даже если не подходит под фильтр.
  const selectedCp = uniqueList.find((c) => c.id === value);
  const optionList =
    selectedCp && !filtered.some((c) => c.id === value)
      ? [selectedCp, ...filtered]
      : filtered;

  return (
    <div className="space-y-1.5">
      {list.length > 8 && (
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="🔍 Поиск контрагента по названию…"
          enterKeyHint="search"
          className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
        />
      )}
      <div className="flex gap-2">
        <select
          value={value ?? ""}
          onChange={(e) =>
            onChange(e.target.value ? parseInt(e.target.value, 10) : null)
          }
          className="flex-1 px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
        >
          <option value="">
            {fNorm
              ? `— найдено ${optionList.length} —`
              : "— выберите —"}
          </option>
          {optionList.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.status === "pending" ? " ⏳" : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
          title="Добавить нового"
        >
          ➕
        </button>
      </div>
      {error && <div className="text-xs text-red-700">{error}</div>}
    </div>
  );
}

// ===================== Purchase form =====================

const CURRENCIES = [
  "USD", "EUR", "CNY", "AED", "GBP", "JPY", "TRY", "HKD",
  "KZT", "CHF", "BYN", "INR", "RUB",
];

function PurchaseView() {
  const [agents, setAgents] = useState<FxAgent[]>([]);
  const [counterpartyId, setCounterpartyId] = useState<number | null>(null);
  const [currency, setCurrency] = useState("USD");
  const [amount, setAmount] = useState("");
  const [rateSource, setRateSource] = useState("cbr");
  const [rateModifier, setRateModifier] = useState("");
  const [cbrRate, setCbrRate] = useState<number | null>(null);
  const [cbrLoading, setCbrLoading] = useState(false);
  const [manualRate, setManualRate] = useState(""); // только для 'special'
  const [agentId, setAgentId] = useState<number | null>(null);
  const [applyDefault, setApplyDefault] = useState(true);
  const [commissionKind, setCommissionKind] = useState<"pct" | "fixed_rub">(
    "pct",
  );
  const [commissionValue, setCommissionValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<FxPurchaseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<FxAgent[]>("/api/fx/agents?activeOnly=true")
      .then(setAgents)
      .catch(() => setAgents([]));
  }, []);

  // Получить ЦБ-курс при смене валюты.
  useEffect(() => {
    if (rateSource === "special") return;
    setCbrLoading(true);
    setCbrRate(null);
    api
      .get<{ currency: string; rate: number | null }>(
        `/api/fx/cbr_rate?currency=${encodeURIComponent(currency)}`,
      )
      .then((r) => setCbrRate(r.rate))
      .catch(() => setCbrRate(null))
      .finally(() => setCbrLoading(false));
  }, [currency, rateSource]);

  // Авто-расчёт итогового курса.
  const finalRate: number | null = (() => {
    if (rateSource === "special") {
      const v = parseDec(manualRate);
      return isNaN(v) || v <= 0 ? null : v;
    }
    if (cbrRate == null) return null;
    if (rateSource === "cbr") return cbrRate;
    const m = parseDec(rateModifier);
    if (isNaN(m) || m < 0) return null;
    return rateSource === "cbr_plus"
      ? cbrRate * (1 + m / 100)
      : cbrRate * (1 - m / 100);
  })();

  const selectedAgent = agents.find((a) => a.id === agentId);

  // Авто-подставить дефолт у выбранного агента (если хотим).
  useEffect(() => {
    if (!selectedAgent || !applyDefault) return;
    if (selectedAgent.default_pct != null) {
      setCommissionKind("pct");
      setCommissionValue(String(selectedAgent.default_pct));
    } else if (selectedAgent.default_fixed_rub != null) {
      setCommissionKind("fixed_rub");
      setCommissionValue(String(selectedAgent.default_fixed_rub));
    }
  }, [agentId, applyDefault]); // eslint-disable-line

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setResult(null);
    haptic("light");
    try {
      if (!counterpartyId) throw new Error("Выберите контрагента");
      if (!amount) throw new Error("Введите сумму");
      if (finalRate == null) throw new Error("Курс не определён");

      const body: Record<string, unknown> = {
        counterparty_id: counterpartyId,
        currency: currency.toUpperCase(),
        amount: parseDec(amount),
        rate_to_rub: finalRate,
        rate_source: rateSource,
      };
      if (rateSource === "cbr_plus" || rateSource === "cbr_minus") {
        body.rate_modifier_pct = parseDec(rateModifier);
      }
      if (agentId) body.agent_id = agentId;
      if (commissionValue && agentId) {
        body.commission_kind = commissionKind;
        body.commission_value = parseDec(commissionValue);
      }
      const r = await api.post<FxPurchaseResponse>("/api/fx/purchases", body);
      notifyHaptic("success");
      setResult(r);
      // reset
      setAmount("");
      setCommissionValue("");
      setManualRate("");
      setRateModifier("");
    } catch (err) {
      notifyHaptic("error");
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <Field label="Контрагент-экспортёр">
        <CounterpartySelector value={counterpartyId} onChange={setCounterpartyId} />
      </Field>

      <Field label="Валюта">
        <select
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
        >
          {CURRENCIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Сумма получаемой валюты">
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          enterKeyHint="next"
          placeholder="100000"
          className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
        />
      </Field>

      <Field label="Источник курса">
        <select
          value={rateSource}
          onChange={(e) => setRateSource(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
        >
          <option value="cbr">📈 Курс ЦБ</option>
          <option value="cbr_plus">➕ ЦБ + X %</option>
          <option value="cbr_minus">➖ ЦБ − X %</option>
          <option value="special">✏️ Свой курс (спец)</option>
        </select>
      </Field>

      {(rateSource === "cbr" ||
        rateSource === "cbr_plus" ||
        rateSource === "cbr_minus") && (
        <div className="text-xs text-tg-hint -mt-1">
          {cbrLoading
            ? "Загружаем курс ЦБ…"
            : cbrRate != null
            ? `Курс ЦБ на сегодня: ${cbrRate.toFixed(4)} ₽/${currency}`
            : "⚠️ Курс ЦБ недоступен — выберите «Свой курс»"}
        </div>
      )}

      {(rateSource === "cbr_plus" || rateSource === "cbr_minus") && (
        <Field label="Модификатор, %">
          <input
            value={rateModifier}
            onChange={(e) => setRateModifier(e.target.value)}
            inputMode="decimal"
            enterKeyHint="next"
            placeholder="0.5"
            className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
          />
        </Field>
      )}

      {rateSource === "special" && (
        <Field label="Курс (RUB за 1 единицу валюты)">
          <input
            value={manualRate}
            onChange={(e) => setManualRate(e.target.value)}
            inputMode="decimal"
            enterKeyHint="next"
            placeholder="92.5"
            className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
          />
        </Field>
      )}

      {finalRate != null && (
        <div className="px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-900">
          💱 Итоговый курс:{" "}
          <span className="font-semibold tabular-nums">
            {finalRate.toFixed(6)} ₽/{currency}
          </span>
        </div>
      )}

      <Field label="Агент-посредник — комиссия (опц.)">
        <select
          value={agentId ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            setAgentId(v ? parseInt(v, 10) : null);
            setApplyDefault(true);
          }}
          className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
        >
          <option value="">— без агента —</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
              {a.default_pct != null &&
                ` (дефолт ${Number(a.default_pct).toFixed(4)}%)`}
              {a.default_fixed_rub != null && ` (дефолт ${a.default_fixed_rub} ₽)`}
            </option>
          ))}
        </select>
      </Field>

      {selectedAgent &&
        (selectedAgent.default_pct != null ||
          selectedAgent.default_fixed_rub != null) && (
          <label className="flex items-center gap-2 text-xs text-tg-hint">
            <input
              type="checkbox"
              checked={applyDefault}
              onChange={(e) => setApplyDefault(e.target.checked)}
            />
            Применить дефолтную ставку агента
          </label>
        )}

      {agentId && (
        <>
          <Field label="Тип комиссии">
            <select
              value={commissionKind}
              onChange={(e) => {
                setCommissionKind(e.target.value as "pct" | "fixed_rub");
                setApplyDefault(false);
              }}
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
            >
              <option value="pct">% от объёма (в RUB)</option>
              <option value="fixed_rub">Фикс. сумма в RUB</option>
            </select>
          </Field>
          <Field
            label={
              commissionKind === "pct" ? "Процент комиссии" : "Сумма в RUB"
            }
          >
            <input
              value={commissionValue}
              onChange={(e) => {
                setCommissionValue(e.target.value);
                setApplyDefault(false);
              }}
              inputMode="decimal"
              enterKeyHint="done"
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
            />
          </Field>
        </>
      )}

      {error && (
        <div className="p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">
          {error}
        </div>
      )}
      {result && (
        <div className="p-2 rounded-lg bg-emerald-50 text-emerald-900 text-xs border border-emerald-200">
          ✅ Лот #{result.lot.id} создан: {result.lot.initial_amount}{" "}
          {result.lot.currency} @ {result.lot.rate_to_rub.toFixed(4)} ₽
          {result.commission_rub != null && (
            <> · комиссия {result.commission_rub.toFixed(2)} ₽</>
          )}
          <div className="text-tg-hint mt-1">
            Лот на балансе экспортёра. Распределите его во вкладке «Распределение».
          </div>
        </div>
      )}

      <button
        type="submit"
        disabled={
          submitting ||
          !counterpartyId ||
          !amount ||
          finalRate == null
        }
        className="w-full px-3 py-2.5 rounded-lg bg-tg-button text-tg-buttonText font-medium disabled:opacity-50"
      >
        {submitting ? "Регистрирую…" : "✅ Зарегистрировать покупку"}
      </button>
    </form>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-xs text-tg-hint block mb-0.5">{label}</label>
      {children}
    </div>
  );
}

// ===================== Distribute =====================

function DistributeView() {
  const [lots, setLots] = useState<FxLot[] | null>(null);
  const [groups, setGroups] = useState<PaymentsResponse["groups"] | null>(null);
  const [selectedLotId, setSelectedLotId] = useState<number | null>(null);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [bankId, setBankId] = useState<number | null>(null);
  // Упорядоченный список выбранных заявок — порядок = приоритет покрытия.
  const [order, setOrder] = useState<number[]>([]);
  const [rates, setRates] = useState<Record<number, string>>({});
  const [coverage, setCoverage] = useState<Record<number, number>>({});
  const [remainderPolicy, setRemainderPolicy] = useState<
    "company_bank" | "counterparty"
  >("company_bank");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<string[]>([]);

  const load = async () => {
    setLots(null);
    setGroups(null);
    setError(null);
    try {
      const [l, p] = await Promise.all([
        api.get<FxLot[]>(
          "/api/fx/lots?holderKind=counterparty&onlyWithRemainder=true",
        ),
        api.get<PaymentsResponse>("/api/payments"),
      ]);
      setLots(l);
      setGroups(p.groups);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Подтягиваем уже-покрытие для заявок выбранной пары.
  useEffect(() => {
    if (companyId == null || bankId == null || !groups) {
      setCoverage({});
      return;
    }
    const pg = groups.find(
      (g) => g.company_id === companyId && g.bank_id === bankId,
    );
    const ids = (pg?.items || [])
      .filter((d) => d.state === "awaiting_payment")
      .map((d) => d.id);
    if (ids.length === 0) {
      setCoverage({});
      return;
    }
    let cancelled = false;
    api
      .get<{ deal_id: number; covered: number }[]>(
        `/api/fx/coverage?deal_ids=${ids.join(",")}`,
      )
      .then((rows) => {
        if (cancelled) return;
        const m: Record<number, number> = {};
        for (const r of rows) m[r.deal_id] = r.covered;
        setCoverage(m);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [companyId, bankId, groups]);

  if (!lots || !groups)
    return <SkeletonRows />;
  if (lots.length === 0)
    return (
      <div className="text-tg-hint text-sm py-8 text-center">
        Нет нераспределённых лотов на балансе экспортёров. Сначала
        зарегистрируйте покупку.
      </div>
    );

  const selectedLot = lots.find((l) => l.id === selectedLotId) || null;

  // Компании из реестра платежей (только пары с валидными id).
  const companyMap = new Map<number, string>();
  for (const g of groups) {
    if (g.company_id != null)
      companyMap.set(g.company_id, g.company_name || `Компания #${g.company_id}`);
  }
  const companies = [...companyMap.entries()].sort((a, b) =>
    a[1].localeCompare(b[1], "ru"),
  );

  // Банки выбранной компании.
  const bankMap = new Map<number, string>();
  if (companyId != null) {
    for (const g of groups) {
      if (g.company_id === companyId && g.bank_id != null)
        bankMap.set(g.bank_id, g.bank_name || `Банк #${g.bank_id}`);
    }
  }
  const banks = [...bankMap.entries()].sort((a, b) =>
    a[1].localeCompare(b[1], "ru"),
  );

  const pairGroup =
    companyId != null && bankId != null
      ? groups.find(
          (g) => g.company_id === companyId && g.bank_id === bankId,
        )
      : undefined;
  const dealsForPair = (pairGroup?.items || []).filter(
    (d) => d.state === "awaiting_payment",
  );
  const needOf = (d: PaymentItem) =>
    Math.max(d.amount - (coverage[d.id] || 0), 0);
  // Защита от повторной привязки: заявки, уже покрытые целиком, выбрать
  // нельзя — они вынесены отдельным списком.
  const openDeals = dealsForPair.filter((d) => needOf(d) > 1e-6);
  const coveredDeals = dealsForPair.filter((d) => needOf(d) <= 1e-6);

  const toggle = (id: number) =>
    setOrder((arr) =>
      arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id],
    );
  const move = (idx: number, dir: -1 | 1) =>
    setOrder((arr) => {
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return arr;
      const next = [...arr];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  // Проекция распределения по выбранному порядку — что покроется.
  const proj: Record<
    number,
    { takeDeal: number; need: number; status: "full" | "partial" | "none" }
  > = {};
  let shortfall = false;
  if (selectedLot) {
    let rem = selectedLot.remaining_amount;
    for (const id of order) {
      const d = dealsForPair.find((x) => x.id === id);
      if (!d) continue;
      const need = needOf(d);
      const sameCur =
        d.currency.toUpperCase() === selectedLot.currency.toUpperCase();
      const rate = parseFloat(rates[id] || "");
      const needLot = sameCur
        ? need
        : !isNaN(rate) && rate > 0
        ? need * rate
        : NaN;
      if (isNaN(needLot)) {
        proj[id] = { takeDeal: 0, need, status: "none" };
        shortfall = true;
        continue;
      }
      const takeLot = Math.min(Math.max(rem, 0), needLot);
      const takeDeal = sameCur ? takeLot : rate > 0 ? takeLot / rate : 0;
      rem -= takeLot;
      const status =
        takeDeal >= need - 1e-6
          ? "full"
          : takeDeal > 1e-6
          ? "partial"
          : "none";
      if (status !== "full") shortfall = true;
      proj[id] = { takeDeal, need, status };
    }
  }

  const submit = async () => {
    if (!selectedLot || companyId == null || bankId == null) return;
    setSubmitting(true);
    setError(null);
    setResults([]);
    haptic("light");
    try {
      if (order.length === 0) {
        // Просто перенести лот на пару без привязки к заявке.
        const r = await api.post<{ unattached_lot?: { id: number } }>(
          "/api/fx/distributions",
          {
            lot_id: selectedLot.id,
            target_company_id: companyId,
            target_bank_id: bankId,
          },
        );
        setResults([
          `Лот перенесён на пару общим лотом ${
            r.unattached_lot ? "#" + r.unattached_lot.id : ""
          }`,
        ]);
        notifyHaptic("success");
        await load();
        return;
      }
      // Конверсионные заявки без курса не отправляем — они остаются в
      // реестре и видны во вкладке «Ждут курс».
      const payload: { deal_id: number; convert_rate?: number }[] = [];
      const skipped: number[] = [];
      for (const id of order) {
        const d = dealsForPair.find((x) => x.id === id);
        if (!d) continue;
        const sameCur =
          d.currency.toUpperCase() === selectedLot.currency.toUpperCase();
        if (sameCur) {
          payload.push({ deal_id: id });
          continue;
        }
        const rate = parseFloat(rates[id] || "");
        if (isNaN(rate) || rate <= 0) {
          skipped.push(id);
          continue;
        }
        payload.push({ deal_id: id, convert_rate: rate });
      }
      if (payload.length === 0) {
        setError(
          "Все выбранные заявки требуют курс конвертации — впишите курс.",
        );
        setSubmitting(false);
        return;
      }
      const r = await api.post<DistributeMultiResponse>(
        "/api/fx/distribute_multi",
        {
          lot_id: selectedLot.id,
          target_company_id: companyId,
          target_bank_id: bankId,
          deals: payload,
          remainder_policy: remainderPolicy,
        },
      );
      const msgs: string[] = [];
      for (const a of r.allocations) {
        msgs.push(
          `Заявка №${a.deal_id}: ${
            a.fully_covered ? "покрыта полностью" : "покрыта частично"
          } — ${a.allocated_amount.toLocaleString("ru-RU")} ${
            a.deal_currency
          }` +
            (a.fully_covered
              ? ""
              : ` из ${a.needed_amount.toLocaleString("ru-RU")}`),
        );
      }
      if (r.uncovered_deal_ids.length > 0)
        msgs.push(
          `⚠️ Лот закончился — не покрыты заявки: ${r.uncovered_deal_ids
            .map((x) => "№" + x)
            .join(", ")}. Распределите на них другой лот.`,
        );
      if (r.already_covered_deal_ids.length > 0)
        msgs.push(
          `Уже были покрыты ранее: ${r.already_covered_deal_ids
            .map((x) => "№" + x)
            .join(", ")}`,
        );
      if (r.surplus_amount > 1e-6)
        msgs.push(
          `Остаток лота ${r.surplus_amount.toLocaleString("ru-RU")} ${
            selectedLot.currency
          } → ${
            remainderPolicy === "counterparty"
              ? "возвращён на баланс экспортёра"
              : "общий лот на паре"
          }`,
        );
      if (skipped.length > 0)
        msgs.push(
          `Без курса (не распределены, см. вкладку «Ждут курс»): ${skipped
            .map((x) => "№" + x)
            .join(", ")}`,
        );
      setResults(msgs);
      notifyHaptic("success");
      setOrder([]);
      await load();
    } catch (err) {
      notifyHaptic("error");
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-900">
        📦 Выберите лот экспортёра, компанию и банк — появится список заявок
        реестра. Отметьте заявки: один лот можно распределить на несколько
        заявок сразу. Стрелками задайте приоритет покрытия — при нехватке
        лота непокрытыми останутся заявки снизу.
      </div>

      {error && (
        <div className="p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">
          {error}
        </div>
      )}
      {results.length > 0 && (
        <div className="p-2 rounded-lg bg-emerald-50 text-emerald-900 text-xs border border-emerald-200 space-y-0.5">
          {results.map((m, i) => (
            <div key={i}>• {m}</div>
          ))}
        </div>
      )}

      <Field label="Лот экспортёра">
        <select
          value={selectedLotId ?? ""}
          onChange={(e) => {
            setSelectedLotId(
              e.target.value ? parseInt(e.target.value, 10) : null,
            );
            setOrder([]);
          }}
          className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
        >
          <option value="">— выберите —</option>
          {lots.map((l) => (
            <option key={l.id} value={l.id}>
              #{l.id}: {l.remaining_amount.toLocaleString("ru-RU")}{" "}
              {l.currency} @ {l.rate_to_rub.toFixed(4)} ₽ · экспортёр #
              {l.holder_id_counterparty}
            </option>
          ))}
        </select>
      </Field>

      {selectedLot && (
        <>
          <Field label="Компания-плательщик">
            <select
              value={companyId ?? ""}
              onChange={(e) => {
                setCompanyId(
                  e.target.value ? parseInt(e.target.value, 10) : null,
                );
                setBankId(null);
                setOrder([]);
              }}
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
            >
              <option value="">— выберите —</option>
              {companies.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </Field>

          {companyId != null && (
            <Field label="Банк компании">
              <select
                value={bankId ?? ""}
                onChange={(e) => {
                  setBankId(
                    e.target.value ? parseInt(e.target.value, 10) : null,
                  );
                  setOrder([]);
                }}
                className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
              >
                <option value="">— выберите —</option>
                {banks.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {companyId != null && bankId != null && (
            <div>
              <div className="text-xs text-tg-hint mb-1">
                Заявки к покрытию ({openDeals.length})
              </div>
              {openDeals.length === 0 ? (
                <div className="text-tg-hint text-sm py-3 text-center bg-tg-sectionBg rounded-xl">
                  В реестре нет заявок к покрытию. Лот можно перенести на
                  пару без привязки кнопкой ниже.
                </div>
              ) : (
                <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
                  {openDeals.map((d) => {
                    const isSel = order.includes(d.id);
                    const sameCur =
                      d.currency.toUpperCase() ===
                      selectedLot.currency.toUpperCase();
                    const cov = coverage[d.id] || 0;
                    return (
                      <div key={d.id} className="px-3 py-2.5">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggle(d.id)}
                            className="mt-0.5"
                          />
                          <div className="min-w-0 flex-1 text-sm">
                            <div className="font-medium">
                              Заявка №{d.id} ·{" "}
                              {d.amount.toLocaleString("ru-RU")} {d.currency}
                            </div>
                            <div className="text-xs text-tg-hint">
                              {d.counterparty_name || "контрагент —"}
                              {d.deal_format ? ` · ${d.deal_format}` : ""}
                            </div>
                            {cov > 1e-6 && (
                              <div className="text-xs text-amber-700">
                                частично покрыта:{" "}
                                {cov.toLocaleString("ru-RU")} /{" "}
                                {d.amount.toLocaleString("ru-RU")}{" "}
                                {d.currency} · осталось{" "}
                                {needOf(d).toLocaleString("ru-RU")}
                              </div>
                            )}
                            <div className="text-xs">
                              {sameCur ? (
                                <span className="text-emerald-700">
                                  валюта совпадает с лотом
                                </span>
                              ) : (
                                <span className="text-amber-700">
                                  нужна конвертация {selectedLot.currency}→
                                  {d.currency}
                                </span>
                              )}
                            </div>
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}

              {coveredDeals.length > 0 && (
                <div className="text-xs text-tg-hint mt-1">
                  ✅ Уже покрыты целиком (повторно привязать нельзя):{" "}
                  {coveredDeals.map((d) => "№" + d.id).join(", ")}
                </div>
              )}

              {order.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs text-tg-hint mb-1">
                    Порядок покрытия ({order.length}) · лот:{" "}
                    {selectedLot.remaining_amount.toLocaleString("ru-RU")}{" "}
                    {selectedLot.currency}
                  </div>
                  <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
                    {order.map((id, idx) => {
                      const d = dealsForPair.find((x) => x.id === id);
                      if (!d) return null;
                      const sameCur =
                        d.currency.toUpperCase() ===
                        selectedLot.currency.toUpperCase();
                      const p = proj[id];
                      return (
                        <div key={id} className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className="flex flex-col leading-none">
                              <button
                                type="button"
                                onClick={() => move(idx, -1)}
                                disabled={idx === 0}
                                className="text-xs disabled:opacity-30"
                              >
                                ▲
                              </button>
                              <button
                                type="button"
                                onClick={() => move(idx, 1)}
                                disabled={idx === order.length - 1}
                                className="text-xs disabled:opacity-30"
                              >
                                ▼
                              </button>
                            </div>
                            <div className="min-w-0 flex-1 text-sm">
                              <div className="font-medium">
                                {idx + 1}. Заявка №{d.id} ·{" "}
                                {needOf(d).toLocaleString("ru-RU")}{" "}
                                {d.currency}
                              </div>
                              {p && (
                                <div
                                  className={
                                    "text-xs " +
                                    (p.status === "full"
                                      ? "text-emerald-700"
                                      : p.status === "partial"
                                      ? "text-amber-700"
                                      : "text-red-700")
                                  }
                                >
                                  {p.status === "full"
                                    ? "покроется полностью"
                                    : p.status === "partial"
                                    ? `покроется частично: ${p.takeDeal.toLocaleString(
                                        "ru-RU",
                                        { maximumFractionDigits: 2 },
                                      )} из ${p.need.toLocaleString("ru-RU")}`
                                    : "не покроется — лота не хватает / не вписан курс"}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => toggle(id)}
                              className="text-xs text-tg-hint px-1"
                            >
                              ✕
                            </button>
                          </div>
                          {!sameCur && (
                            <div className="mt-1.5 pl-7">
                              <input
                                value={rates[id] || ""}
                                onChange={(e) =>
                                  setRates((pp) => ({
                                    ...pp,
                                    [id]: e.target.value,
                                  }))
                                }
                                inputMode="decimal"
                                placeholder={`Курс: сколько ${selectedLot.currency} за 1 ${d.currency}`}
                                className="w-full px-3 py-1.5 rounded-lg bg-tg-secondaryBg text-sm"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {shortfall && (
                    <div className="mt-2 p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">
                      ⚠️ Лота не хватает на все выбранные заявки (или не
                      вписан курс конвертации). Заявки со статусом «частично»
                      / «не покроется» останутся недопокрытыми — измените
                      порядок стрелками или снимите лишние. Непокрытый
                      остаток можно закрыть другим лотом позже.
                    </div>
                  )}
                </div>
              )}

              <Field label="Что делать с остатком лота">
                <select
                  value={remainderPolicy}
                  onChange={(e) =>
                    setRemainderPolicy(
                      e.target.value as "company_bank" | "counterparty",
                    )
                  }
                  className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm mt-2"
                >
                  <option value="company_bank">
                    Распределить на эту компанию и банк (общий лот)
                  </option>
                  <option value="counterparty">
                    Оставить у экспортёра (вернуть на его баланс)
                  </option>
                </select>
              </Field>

              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="w-full mt-3 px-3 py-2.5 rounded-lg bg-tg-button text-tg-buttonText font-medium disabled:opacity-50"
              >
                {submitting
                  ? "Распределяю…"
                  : order.length > 0
                  ? `📦 Распределить на ${order.length} заявк${
                      order.length === 1 ? "у" : "и"
                    }`
                  : "📦 Перенести лот на пару (без привязки)"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ===================== Awaiting rate =====================

function AwaitingRateView() {
  const [pending, setPending] = useState<FxPendingAttach[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const r = await api.get<FxPendingAttach[]>("/api/fx/pending_attaches");
      setPending(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-3">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-900">
        ⏳ Заявки, привязанные оператором к экспорт-лоту, по которым ещё
        не проставлен кросс-курс конвертации. Заявки, списываемые по FIFO
        при оплатах и конвертациях, здесь не показываются.
      </div>

      {error && (
        <div className="p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">
          {error}
        </div>
      )}

      {!adding ? (
        <button
          onClick={() => setAdding(true)}
          className="w-full px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm"
        >
          ➕ Привязать заявку к экспорт-лоту
        </button>
      ) : (
        <PendingAttachForm
          onDone={() => {
            setAdding(false);
            load();
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      {pending === null ? (
        <SkeletonRows />
      ) : pending.length === 0 ? (
        <div className="text-tg-hint text-sm py-8 text-center">
          Нет заявок, ожидающих кросс-курс.
        </div>
      ) : (
        <div className="space-y-2">
          {pending.map((pa) => (
            <PendingAttachRow key={pa.id} pa={pa} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function PendingAttachForm({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}) {
  const [deals, setDeals] = useState<PaymentItem[]>([]);
  const [lots, setLots] = useState<FxLot[]>([]);
  const [dealId, setDealId] = useState<number | null>(null);
  const [lotId, setLotId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, l] = await Promise.all([
          api.get<PaymentsResponse>("/api/payments"),
          api.get<FxLot[]>(
            "/api/fx/lots?holderKind=counterparty&onlyWithRemainder=true",
          ),
        ]);
        setDeals(
          p.groups.flatMap((g) =>
            (g.items || []).filter((d) => d.state === "awaiting_payment"),
          ),
        );
        setLots(l);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  const deal = deals.find((d) => d.id === dealId) || null;
  const lot = lots.find((l) => l.id === lotId) || null;
  const sameCurrency =
    deal != null && lot != null && deal.currency === lot.currency;

  const submit = async () => {
    if (dealId == null || lotId == null) return;
    setSaving(true);
    setError(null);
    try {
      await api.post("/api/fx/pending_attaches", {
        deal_id: dealId,
        lot_id: lotId,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const selCls =
    "w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm";

  return (
    <div className="bg-tg-sectionBg rounded-xl p-3 space-y-2">
      <div className="text-xs font-medium">
        Привязка заявки к экспорт-лоту без курса
      </div>
      <select
        value={dealId ?? ""}
        onChange={(e) =>
          setDealId(e.target.value ? parseInt(e.target.value, 10) : null)
        }
        className={selCls}
      >
        <option value="">— заявка реестра —</option>
        {deals.map((d) => (
          <option key={d.id} value={d.id}>
            №{d.id} · {d.amount.toLocaleString("ru-RU")} {d.currency}
            {d.counterparty_name ? ` · ${d.counterparty_name}` : ""}
          </option>
        ))}
      </select>
      <select
        value={lotId ?? ""}
        onChange={(e) =>
          setLotId(e.target.value ? parseInt(e.target.value, 10) : null)
        }
        className={selCls}
      >
        <option value="">— экспорт-лот —</option>
        {lots.map((l) => (
          <option key={l.id} value={l.id}>
            Лот #{l.id} · {l.remaining_amount.toLocaleString("ru-RU")}{" "}
            {l.currency} @ {l.rate_to_rub.toFixed(4)}
          </option>
        ))}
      </select>
      {sameCurrency && (
        <div className="text-xs text-amber-700">
          Валюта лота совпадает с валютой заявки — кросс-курс не нужен,
          используйте «Распределение».
        </div>
      )}
      {error && <div className="text-xs text-red-700">{error}</div>}
      <div className="flex gap-2">
        <button
          onClick={submit}
          disabled={
            saving || dealId == null || lotId == null || sameCurrency
          }
          className="flex-1 px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
        >
          Привязать
        </button>
        <button
          onClick={onCancel}
          className="px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
        >
          Отмена
        </button>
      </div>
    </div>
  );
}

function PendingAttachRow({
  pa,
  onChanged,
}: {
  pa: FxPendingAttach;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [amountFrom, setAmountFrom] = useState("");
  const [amountTo, setAmountTo] = useState(String(pa.deal_amount));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const resolve = async () => {
    if (pa.deal_company_id == null || pa.deal_bank_id == null) {
      setError("У заявки не заданы компания и банк — конверсия невозможна.");
      return;
    }
    const from = parseDec(amountFrom);
    const to = parseDec(amountTo);
    if (!(from > 0) || !(to > 0)) {
      setError("Введите положительные суммы конвертации.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/fx/pending_attaches/${pa.deal_id}/resolve`, {
        target_company_id: pa.deal_company_id,
        target_bank_id: pa.deal_bank_id,
        convert_amount_from: from,
        convert_amount_to: to,
        surplus_policy: "company_bank",
      });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.del(`/api/fx/pending_attaches/${pa.deal_id}`);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const from = parseDec(amountFrom);
  const to = parseDec(amountTo);
  const crossRate = from > 0 && to > 0 ? from / to : null;

  return (
    <div className="bg-tg-sectionBg rounded-xl p-3 text-sm">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-medium">
          Заявка №{pa.deal_id} · {pa.deal_amount.toLocaleString("ru-RU")}{" "}
          {pa.deal_currency}
        </span>
        <StatusPill tone="warning">ждёт курс</StatusPill>
      </div>
      <div className="text-xs text-tg-hint mt-0.5">
        {pa.deal_counterparty_name || "контрагент —"}
        {pa.deal_company_name ? ` · ${pa.deal_company_name}` : ""}
        {pa.deal_bank_name ? ` · ${pa.deal_bank_name}` : ""}
      </div>
      <div className="text-xs text-tg-hint mt-0.5">
        Экспорт-лот #{pa.lot_id}: {pa.lot_remaining.toLocaleString("ru-RU")}{" "}
        {pa.lot_currency} @ {pa.lot_rate_to_rub.toFixed(4)} ₽
      </div>

      {!open ? (
        <div className="flex gap-4 mt-2">
          <button
            onClick={() => setOpen(true)}
            className="text-tg-link text-xs"
          >
            Проставить кросс-курс
          </button>
          <button
            onClick={cancel}
            disabled={busy}
            className="text-tg-destructive text-xs disabled:opacity-50"
          >
            Снять привязку
          </button>
        </div>
      ) : (
        <div className="mt-2 pt-2 border-t border-tg-secondaryBg space-y-2">
          <div className="text-xs text-tg-hint">
            Конверсия {pa.lot_currency} → {pa.deal_currency}
          </div>
          <label className="block text-xs text-tg-hint">
            Списываем из лота, {pa.lot_currency}
            <input
              value={amountFrom}
              onChange={(e) => setAmountFrom(e.target.value)}
              inputMode="decimal"
              placeholder="0"
              className="mt-0.5 w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            />
          </label>
          <label className="block text-xs text-tg-hint">
            Получаем для заявки, {pa.deal_currency}
            <input
              value={amountTo}
              onChange={(e) => setAmountTo(e.target.value)}
              inputMode="decimal"
              className="mt-0.5 w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            />
          </label>
          {crossRate != null && (
            <div className="text-xs text-tg-hint">
              Кросс-курс: 1 {pa.deal_currency} = {crossRate.toFixed(6)}{" "}
              {pa.lot_currency}
            </div>
          )}
          {error && <div className="text-xs text-red-700">{error}</div>}
          <div className="flex gap-2">
            <button
              onClick={resolve}
              disabled={busy}
              className="flex-1 px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
            >
              Провести конверсию
            </button>
            <button
              onClick={() => setOpen(false)}
              className="px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
            >
              Отмена
            </button>
          </div>
        </div>
      )}
      {!open && error && (
        <div className="text-xs text-red-700 mt-1">{error}</div>
      )}
    </div>
  );
}

// ===================== Обмен валюты =====================

type ExchangeMode =
  | "intrabank"
  | "provider"
  | "providers"
  | "active"
  | "ledger"
  | "pool"
  | "wallets"
  | "addrbook"
  | "cryptotx"
  | "test";

type ExchangeCluster = "deals" | "crypto" | "books";

// Двухуровневая навигация «Обмена»: 10 под-вкладок сгруппированы в 3
// смысловых кластера, чтобы убрать перегруженную плоскую ленту.
const EXCHANGE_CLUSTERS: Array<{
  key: ExchangeCluster;
  label: string;
  icon: IconName;
  accent: string;
  tabs: Array<[ExchangeMode, string]>;
}> = [
  {
    key: "deals",
    label: "Обмен",
    icon: "swap",
    accent: "#7c3aed",
    tabs: [
      ["intrabank", "🏦 Внутрибанковский"],
      ["provider", "🌐 Через провайдера"],
      ["providers", "👥 Провайдеры"],
    ],
  },
  {
    key: "crypto",
    label: "Крипта",
    icon: "coins",
    accent: "#0891b2",
    tabs: [
      ["pool", "💎 Крипто-пул"],
      ["cryptotx", "📥 Транзакции"],
      ["wallets", "👛 Кошельки"],
      ["addrbook", "📇 Адресная книга"],
      ["test", "🧪 Тест-перевод"],
    ],
  },
  {
    key: "books",
    label: "Учёт",
    icon: "ledger",
    accent: "#0d9488",
    tabs: [
      ["active", "⏳ Активные"],
      ["ledger", "📒 Расчёты"],
    ],
  },
];

export function ExchangeView() {
  const [cluster, setCluster] = useState<ExchangeCluster>("deals");
  const [mode, setMode] = useState<ExchangeMode>("intrabank");
  const active = EXCHANGE_CLUSTERS.find((c) => c.key === cluster)!;

  return (
    <div className="space-y-3">
      <h1 className="text-xl font-semibold">Обмен валюты</h1>
      {/* Уровень 1 — кластеры разделов. */}
      <div className="flex gap-2">
        {EXCHANGE_CLUSTERS.map((c) => {
          const on = c.key === cluster;
          return (
            <button
              key={c.key}
              onClick={() => {
                setCluster(c.key);
                setMode(c.tabs[0][0]);
              }}
              className={
                "flex-1 py-2 rounded-xl flex flex-col items-center gap-1 " +
                "text-xs font-medium " +
                (on ? "" : "bg-tg-secondaryBg text-tg-hint")
              }
              style={
                on
                  ? { backgroundColor: c.accent + "22", color: c.accent }
                  : undefined
              }
            >
              <Icon name={c.icon} size={20} />
              {c.label}
            </button>
          );
        })}
      </div>
      {/* Уровень 2 — под-вкладки выбранного кластера. */}
      <div className="flex flex-wrap gap-2">
        {active.tabs.map(([m, label]) => {
          const on = mode === m;
          return (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={
                "px-3 py-1.5 rounded-full text-xs whitespace-nowrap " +
                (on ? "text-white" : "bg-tg-secondaryBg text-tg-hint")
              }
              style={on ? { backgroundColor: active.accent } : undefined}
            >
              {label}
            </button>
          );
        })}
      </div>
      {mode === "intrabank" && <IntrabankExchange />}
      {mode === "provider" && <ProviderExchangeForm />}
      {mode === "test" && <TestTransferHelper />}
      {mode === "active" && <ActiveExchanges />}
      {mode === "ledger" && <ProviderLedgerView />}
      {mode === "pool" && <CryptoPoolView />}
      {mode === "cryptotx" && <CryptoTxView />}
      {mode === "wallets" && <WalletsManage />}
      {mode === "addrbook" && <AddressBook />}
      {mode === "providers" && <ProvidersManage />}
    </div>
  );
}

function IntrabankExchange() {
  const [balances, setBalances] = useState<FxBalance[] | null>(null);
  const [coNames, setCoNames] = useState<Record<number, string>>({});
  const [bkNames, setBkNames] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);

  const [srcKey, setSrcKey] = useState("");
  const [toCurrency, setToCurrency] = useState("RUB");
  const [amountSent, setAmountSent] = useState("");
  const [rate, setRate] = useState("");
  const [hasCommission, setHasCommission] = useState(false);
  const [commission, setCommission] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      const [b, p] = await Promise.all([
        api.get<FxBalance[]>("/api/fx/balances?includeAttached=false"),
        api.get<PaymentsResponse>("/api/payments"),
      ]);
      setBalances(
        b.filter((x) => x.holder_kind === "company_bank" && x.total_amount > 0),
      );
      const co: Record<number, string> = {};
      const bk: Record<number, string> = {};
      for (const g of p.groups) {
        if (g.company_id != null && g.company_name)
          co[g.company_id] = g.company_name;
        if (g.bank_id != null && g.bank_name) bk[g.bank_id] = g.bank_name;
      }
      setCoNames(co);
      setBkNames(bk);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const keyOf = (b: FxBalance) =>
    `${b.holder_id_company}|${b.holder_id_bank}|${b.currency}`;
  const src = (balances || []).find((b) => keyOf(b) === srcKey) || null;

  const sentNum = parseDec(amountSent);
  const rateNum = parseDec(rate);
  const commNum = hasCommission ? parseDec(commission) : 0;
  const expected = sentNum > 0 && rateNum > 0 ? sentNum * rateNum : null;
  const finalTo =
    expected != null ? expected - (commNum > 0 ? commNum : 0) : null;

  const submit = async () => {
    if (!src || finalTo == null) return;
    if (finalTo <= 0) {
      setError("Итоговая сумма к получению должна быть больше нуля.");
      return;
    }
    if (sentNum > src.total_amount + 1e-9) {
      setError("Сумма отправки превышает остаток на балансе.");
      return;
    }
    if (toCurrency.trim().toUpperCase() === src.currency) {
      setError("Валюта получения совпадает с исходной.");
      return;
    }
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.post<{ new_lot: { id: number } }>(
        "/api/fx/conversions",
        {
          company_id: src.holder_id_company,
          bank_id: src.holder_id_bank,
          from_currency: src.currency,
          amount_from: sentNum,
          to_currency: toCurrency.trim().toUpperCase(),
          amount_to: finalTo,
        },
      );
      setResult(
        `Обмен проведён: получено ${finalTo.toLocaleString("ru-RU")} ` +
          `${toCurrency.trim().toUpperCase()} (лот #${r.new_lot.id}).`,
      );
      setAmountSent("");
      setRate("");
      setCommission("");
      setHasCommission(false);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    !submitting && !!src && finalTo != null && finalTo > 0 && sentNum > 0;
  useMainButton({
    text: submitting ? "Проводим…" : "Провести обмен",
    onClick: submit,
    enabled: canSubmit,
    progress: submitting,
    visible: balances != null && balances.length > 0,
  });

  if (balances === null) return <SkeletonRows />;

  return (
    <div className="space-y-3">
      <div className="p-2.5 rounded-lg bg-tg-sectionBg text-xs text-tg-hint leading-relaxed">
        Внутрибанковский обмен — перевод остатка из одной валюты в другую
        внутри той же пары «компания × банк», без вывода средств наружу.
        Обмен через FX-провайдера будет добавлен следующим этапом.
      </div>

      {balances.length === 0 ? (
        <div className="text-tg-hint text-sm py-8 text-center">
          Нет свободных остатков на балансе «компания × банк» для обмена.
        </div>
      ) : (
        <div className="bg-tg-sectionBg rounded-xl p-3 space-y-2">
          <Field label="Что меняем (остаток компания × банк)">
            <select
              value={srcKey}
              onChange={(e) => setSrcKey(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            >
              <option value="">— выберите остаток —</option>
              {balances.map((b) => (
                <option key={keyOf(b)} value={keyOf(b)}>
                  {coNames[b.holder_id_company ?? -1] ||
                    `Компания #${b.holder_id_company}`}{" "}
                  ·{" "}
                  {bkNames[b.holder_id_bank ?? -1] ||
                    `Банк #${b.holder_id_bank}`}{" "}
                  · {b.total_amount.toLocaleString("ru-RU")} {b.currency}
                </option>
              ))}
            </select>
          </Field>

          <Field label="В какую валюту">
            <select
              value={toCurrency}
              onChange={(e) => setToCurrency(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label={`Сумма отправки${src ? `, ${src.currency}` : ""}`}
          >
            <input
              value={amountSent}
              onChange={(e) => setAmountSent(e.target.value)}
              inputMode="decimal"
              placeholder="0"
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
            />
          </Field>

          <Field label={`Курс банка (1 ${src?.currency || "?"} = X ${toCurrency})`}>
            <input
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              inputMode="decimal"
              placeholder="напр. 92.5"
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
            />
          </Field>

          <label className="flex items-center gap-2 text-xs text-tg-hint">
            <input
              type="checkbox"
              checked={hasCommission}
              onChange={(e) => setHasCommission(e.target.checked)}
            />
            Есть отдельная комиссия банка
          </label>
          {hasCommission && (
            <Field label={`Комиссия, ${toCurrency} (вычитается из поступления)`}>
              <input
                value={commission}
                onChange={(e) => setCommission(e.target.value)}
                inputMode="decimal"
                placeholder="0"
                className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
              />
            </Field>
          )}

          {expected != null && (
            <div className="px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-900">
              Ожидаемое поступление:{" "}
              <span className="font-semibold tabular-nums">
                {expected.toLocaleString("ru-RU", {
                  maximumFractionDigits: 4,
                })}{" "}
                {toCurrency}
              </span>
              {commNum > 0 && finalTo != null && (
                <div className="text-xs mt-0.5">
                  За вычетом комиссии:{" "}
                  <span className="font-semibold tabular-nums">
                    {finalTo.toLocaleString("ru-RU", {
                      maximumFractionDigits: 4,
                    })}{" "}
                    {toCurrency}
                  </span>
                </div>
              )}
            </div>
          )}

          {error && <div className="text-xs text-red-700">{error}</div>}
          {result && (
            <div className="text-xs text-emerald-700">{result}</div>
          )}

          {!isTelegram() && (
            <button
              onClick={submit}
              disabled={!canSubmit}
              className="w-full px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
            >
              {submitting ? "Проводим…" : "Провести обмен"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Обмен через провайдера: вспомогательное ──

interface CoBk {
  companies: Array<[number, string]>;
  banksByCompany: Record<number, Array<[number, string]>>;
  coName: Record<number, string>;
  bkName: Record<number, string>;
}

async function loadCoBk(): Promise<CoBk> {
  const p = await api.get<PaymentsResponse>("/api/payments");
  const coName: Record<number, string> = {};
  const bkName: Record<number, string> = {};
  const banksByCompany: Record<number, Array<[number, string]>> = {};
  const seen: Record<number, Set<number>> = {};
  for (const g of p.groups) {
    if (g.company_id == null) continue;
    coName[g.company_id] = g.company_name || `Компания #${g.company_id}`;
    if (g.bank_id != null) {
      bkName[g.bank_id] = g.bank_name || `Банк #${g.bank_id}`;
      if (!seen[g.company_id]) {
        seen[g.company_id] = new Set();
        banksByCompany[g.company_id] = [];
      }
      if (!seen[g.company_id].has(g.bank_id)) {
        seen[g.company_id].add(g.bank_id);
        banksByCompany[g.company_id].push([
          g.bank_id,
          g.bank_name || `Банк #${g.bank_id}`,
        ]);
      }
    }
  }
  const companies = Object.entries(coName)
    .map(([id, n]) => [parseInt(id, 10), n] as [number, string])
    .sort((a, b) => a[1].localeCompare(b[1], "ru"));
  return { companies, banksByCompany, coName, bkName };
}

function ProvidersManage() {
  const [list, setList] = useState<FxProvider[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [pct, setPct] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setList(null);
    try {
      setList(await api.get<FxProvider[]>("/api/fx/providers?activeOnly=true"));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setEditId(null);
    setName("");
    setPct("");
    setShowForm(false);
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {
        name,
        default_commission_pct: pct ? parseDec(pct) : null,
      };
      if (editId != null) await api.put(`/api/fx/providers/${editId}`, body);
      else await api.post("/api/fx/providers", body);
      reset();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };
  const deactivate = async (id: number) => {
    try {
      await api.del(`/api/fx/providers/${id}`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const inputCls =
    "w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm";
  return (
    <div className="space-y-3">
      {error && (
        <div className="p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">
          {error}
        </div>
      )}
      {!showForm && (
        <button
          onClick={() => {
            reset();
            setShowForm(true);
          }}
          className="w-full px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm"
        >
          ➕ Добавить FX-провайдера
        </button>
      )}
      {showForm && (
        <form onSubmit={submit} className="bg-tg-sectionBg rounded-xl p-3 space-y-2">
          <div className="text-xs font-medium">
            {editId != null ? "Редактирование провайдера" : "Новый провайдер"}
          </div>
          <input
            placeholder="Название провайдера"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputCls}
          />
          <input
            placeholder="Дефолтная комиссия, % (опц.)"
            value={pct}
            onChange={(e) => setPct(e.target.value)}
            inputMode="decimal"
            className={inputCls}
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="flex-1 px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
            >
              Сохранить
            </button>
            <button
              type="button"
              onClick={reset}
              className="px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
            >
              Отмена
            </button>
          </div>
        </form>
      )}
      {list === null ? (
        <SkeletonRows />
      ) : list.length === 0 ? (
        <div className="text-tg-hint text-sm py-6 text-center">
          FX-провайдеры не добавлены.
        </div>
      ) : (
        <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
          {list.map((p) => (
            <div
              key={p.id}
              className="px-4 py-3 flex items-baseline justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="font-medium text-sm">{p.name}</div>
                <div className="text-xs text-tg-hint">
                  {p.default_commission_pct != null
                    ? `комиссия ${Number(p.default_commission_pct).toFixed(4)}% · `
                    : ""}
                  ID #{p.id}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <button
                  onClick={() => {
                    setEditId(p.id);
                    setName(p.name);
                    setPct(
                      p.default_commission_pct != null
                        ? String(p.default_commission_pct)
                        : "",
                    );
                    setShowForm(true);
                  }}
                  className="text-tg-link text-xs"
                >
                  Изменить
                </button>
                <button
                  onClick={() => deactivate(p.id)}
                  className="text-tg-destructive text-xs"
                >
                  Деактивировать
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProviderExchangeForm() {
  const [providers, setProviders] = useState<FxProvider[]>([]);
  const [balances, setBalances] = useState<FxBalance[]>([]);
  const [pool, setPool] = useState<FxPoolBalance[]>([]);
  const [cobk, setCobk] = useState<CoBk | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const [providerId, setProviderId] = useState<number | null>(null);
  const [direction, setDirection] = useState("fiat_fiat");
  const [srcKey, setSrcKey] = useState("");
  const [poolCur, setPoolCur] = useState("");
  const [toCurrency, setToCurrency] = useState("RUB");
  const [amountSent, setAmountSent] = useState("");
  const [rate, setRate] = useState("");
  const [hasComm, setHasComm] = useState(false);
  const [comm, setComm] = useState("");
  const [targetCo, setTargetCo] = useState<number | null>(null);
  const [targetBk, setTargetBk] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [templates, setTemplates] = useState<ExchangeTemplate[]>([]);

  const loadTemplates = async () => {
    try {
      setTemplates(
        await api.get<ExchangeTemplate[]>("/api/fx/exchange_templates"),
      );
    } catch {
      /* шаблоны не критичны — игнорируем */
    }
  };

  const load = async () => {
    try {
      const [pr, bl, pl, cb] = await Promise.all([
        api.get<FxProvider[]>("/api/fx/providers?activeOnly=true"),
        api.get<FxBalance[]>("/api/fx/balances?includeAttached=false"),
        api.get<FxPoolBalance[]>("/api/fx/crypto_pool"),
        loadCoBk(),
      ]);
      setProviders(pr);
      setBalances(
        bl.filter((x) => x.holder_kind === "company_bank" && x.total_amount > 0),
      );
      setPool(pl.filter((x) => x.total_amount > 0));
      setCobk(cb);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    load();
    loadTemplates();
  }, []);

  const applyTemplate = async (t: ExchangeTemplate) => {
    setProviderId(t.provider_id);
    setDirection(t.direction);
    setToCurrency(t.to_currency);
    setTargetCo(t.target_company_id);
    setTargetBk(t.target_bank_id);
    setResult(null);
    setError(null);
    try {
      await api.post(`/api/fx/exchange_templates/${t.id}/use`, {});
    } catch {
      /* счётчик не критичен */
    }
  };

  const saveTemplate = async () => {
    if (providerId == null) {
      setError("Выберите провайдера перед сохранением шаблона.");
      return;
    }
    const name = window.prompt("Название шаблона обмена:");
    if (!name || !name.trim()) return;
    try {
      await api.post("/api/fx/exchange_templates", {
        name: name.trim(),
        provider_id: providerId,
        direction,
        from_currency: fromCurrency || "RUB",
        to_currency: effTo,
        target_company_id: toCryptoPool ? null : targetCo,
        target_bank_id: toCryptoPool ? null : targetBk,
      });
      await loadTemplates();
      setResult("Шаблон сохранён.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  // crypto_fiat — источник крипто-пул; иначе — остаток компания×банк.
  const fromPool = direction === "crypto_fiat";
  const toCryptoPool = direction === "fiat_crypto";

  const keyOf = (b: FxBalance) =>
    `${b.holder_id_company}|${b.holder_id_bank}|${b.currency}`;
  const cbSrc = balances.find((b) => keyOf(b) === srcKey) || null;
  const poolSrc = pool.find((p) => p.currency === poolCur) || null;
  const fromCurrency = fromPool
    ? poolSrc?.currency || ""
    : cbSrc?.currency || "";
  const srcAvailable = fromPool
    ? poolSrc?.total_amount || 0
    : cbSrc?.total_amount || 0;
  const srcReady = fromPool ? poolSrc != null : cbSrc != null;

  const sentNum = parseDec(amountSent);
  const rateNum = parseDec(rate);
  const commNum = hasComm ? parseDec(comm) : 0;
  const effTo = toCryptoPool ? "USDT" : toCurrency.trim().toUpperCase();
  const expected =
    sentNum > 0 && rateNum > 0
      ? sentNum * rateNum - (commNum > 0 ? commNum : 0)
      : null;

  const submit = async () => {
    if (!srcReady || providerId == null) return;
    if (!(sentNum > 0) || !(rateNum > 0)) {
      setError("Заполните сумму отправки и курс.");
      return;
    }
    if (sentNum > srcAvailable + 1e-9) {
      setError("Сумма отправки превышает остаток источника.");
      return;
    }
    if (!toCryptoPool && (targetCo == null || targetBk == null)) {
      setError("Укажите компанию и банк получения.");
      return;
    }
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const ex = await api.post<FxExchange>("/api/fx/exchanges", {
        provider_id: providerId,
        direction,
        source_company_id: fromPool ? null : cbSrc!.holder_id_company,
        source_bank_id: fromPool ? null : cbSrc!.holder_id_bank,
        from_currency: fromCurrency,
        amount_sent: sentNum,
        declared_rate: rateNum,
        to_currency: effTo,
        commission_amount: commNum > 0 ? commNum : null,
        commission_currency: commNum > 0 ? effTo : null,
        target_company_id: toCryptoPool ? null : targetCo,
        target_bank_id: toCryptoPool ? null : targetBk,
      });
      setResult(
        `Обмен #${ex.id} создан. Ожидаемое поступление: ` +
          `${ex.expected_return.toLocaleString("ru-RU")} ${ex.to_currency}. ` +
          `Зафиксируйте возврат во вкладке «Активные».`,
      );
      setAmountSent("");
      setRate("");
      setComm("");
      setHasComm(false);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm";
  const banks =
    targetCo != null && cobk ? cobk.banksByCompany[targetCo] || [] : [];

  const canSubmit =
    !submitting &&
    srcReady &&
    providerId != null &&
    sentNum > 0 &&
    rateNum > 0;
  useMainButton({
    text: submitting ? "Создаём…" : "Создать обмен",
    onClick: submit,
    enabled: canSubmit,
    progress: submitting,
  });

  return (
    <div className="space-y-3">
      <div className="p-2.5 rounded-lg bg-tg-sectionBg text-xs text-tg-hint leading-relaxed">
        Обмен через внешнего FX-провайдера. Вы вводите сумму отправки и
        объявленный курс — система считает ожидаемое поступление. Возврат
        фиксируется отдельно во вкладке «Активные».
      </div>

      {templates.length > 0 && (
        <div>
          <div className="text-xs text-tg-hint mb-1">
            Шаблоны — заполнить маршрут в один тап:
          </div>
          <div className="flex flex-wrap gap-1.5">
            {templates.map((t) => (
              <button
                key={t.id}
                onClick={() => applyTemplate(t)}
                className="px-2.5 py-1 rounded-full bg-tg-secondaryBg text-tg-text text-xs"
              >
                ⭐ {t.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="bg-tg-sectionBg rounded-xl p-3 space-y-2">
        <Field label="FX-провайдер">
          <select
            value={providerId ?? ""}
            onChange={(e) =>
              setProviderId(e.target.value ? parseInt(e.target.value, 10) : null)
            }
            className={inputCls}
          >
            <option value="">— выберите провайдера —</option>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Направление обмена">
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value)}
            className={inputCls}
          >
            <option value="fiat_fiat">Фиат → Фиат</option>
            <option value="fiat_crypto">Фиат → Крипта (USDT в пул)</option>
            <option value="crypto_fiat">Крипта → Фиат</option>
          </select>
        </Field>

        {fromPool ? (
          <Field label="Источник — валюта крипто-пула">
            <select
              value={poolCur}
              onChange={(e) => setPoolCur(e.target.value)}
              className={inputCls}
            >
              <option value="">— выберите валюту пула —</option>
              {pool.map((p) => (
                <option key={p.currency} value={p.currency}>
                  {p.currency} ·{" "}
                  {p.total_amount.toLocaleString("ru-RU")} в пуле
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <Field label="Источник — остаток «компания × банк»">
            <select
              value={srcKey}
              onChange={(e) => setSrcKey(e.target.value)}
              className={inputCls}
            >
              <option value="">— выберите остаток —</option>
              {balances.map((b) => (
                <option key={keyOf(b)} value={keyOf(b)}>
                  {cobk?.coName[b.holder_id_company ?? -1] ||
                    `Компания #${b.holder_id_company}`}{" "}
                  ·{" "}
                  {cobk?.bkName[b.holder_id_bank ?? -1] ||
                    `Банк #${b.holder_id_bank}`}{" "}
                  · {b.total_amount.toLocaleString("ru-RU")} {b.currency}
                </option>
              ))}
            </select>
          </Field>
        )}

        <div className="flex gap-2">
          <Field
            label={`Сумма отправки${fromCurrency ? `, ${fromCurrency}` : ""}`}
          >
            <input
              value={amountSent}
              onChange={(e) => setAmountSent(e.target.value)}
              inputMode="decimal"
              placeholder="0"
              className={inputCls}
            />
          </Field>
          <Field label="Валюта получения">
            {toCryptoPool ? (
              <div className={inputCls + " text-tg-hint"}>USDT (в пул)</div>
            ) : (
              <select
                value={toCurrency}
                onChange={(e) => setToCurrency(e.target.value)}
                className={inputCls}
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>
        <Field
          label={`Объявленный курс (1 ${fromCurrency || "?"} = X ${effTo})`}
        >
          <input
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            inputMode="decimal"
            placeholder="напр. 1.0 или 92.5"
            className={inputCls}
          />
        </Field>
        <label className="flex items-center gap-2 text-xs text-tg-hint">
          <input
            type="checkbox"
            checked={hasComm}
            onChange={(e) => setHasComm(e.target.checked)}
          />
          Есть отдельная комиссия провайдера
        </label>
        {hasComm && (
          <Field label={`Комиссия, ${effTo}`}>
            <input
              value={comm}
              onChange={(e) => setComm(e.target.value)}
              inputMode="decimal"
              placeholder="0"
              className={inputCls}
            />
          </Field>
        )}
        {toCryptoPool && (
          <div className="text-xs text-tg-hint">
            Возврат USDT поступает в общий крипто-пул группы.
          </div>
        )}
        {!toCryptoPool && (
          <div className="flex gap-2">
            <Field label="Компания получения">
              <select
                value={targetCo ?? ""}
                onChange={(e) => {
                  setTargetCo(
                    e.target.value ? parseInt(e.target.value, 10) : null,
                  );
                  setTargetBk(null);
                }}
                className={inputCls}
              >
                <option value="">— выберите —</option>
                {(cobk?.companies || []).map(([id, n]) => (
                  <option key={id} value={id}>
                    {n}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Банк получения">
              <select
                value={targetBk ?? ""}
                onChange={(e) =>
                  setTargetBk(
                    e.target.value ? parseInt(e.target.value, 10) : null,
                  )
                }
                className={inputCls}
                disabled={targetCo == null}
              >
                <option value="">— выберите —</option>
                {banks.map(([id, n]) => (
                  <option key={id} value={id}>
                    {n}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        )}
        {expected != null && (
          <div className="px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-900">
            Ожидаемое поступление:{" "}
            <span className="font-semibold tabular-nums">
              {expected.toLocaleString("ru-RU", { maximumFractionDigits: 4 })}{" "}
              {effTo}
            </span>
          </div>
        )}
        {error && <div className="text-xs text-red-700">{error}</div>}
        {result && <div className="text-xs text-emerald-700">{result}</div>}
        <button
          onClick={saveTemplate}
          className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-xs"
        >
          ⭐ Сохранить маршрут как шаблон
        </button>
        {!isTelegram() && (
          <button
            onClick={submit}
            disabled={!canSubmit}
            className="w-full px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
          >
            {submitting ? "Создаём…" : "Создать обмен"}
          </button>
        )}
      </div>
    </div>
  );
}

function ActiveExchanges() {
  const [list, setList] = useState<FxExchange[] | null>(null);
  const [providers, setProviders] = useState<Record<number, string>>({});
  const [cobk, setCobk] = useState<CoBk | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const [ex, pr, cb] = await Promise.all([
        api.get<FxExchange[]>("/api/fx/exchanges?status=pending"),
        api.get<FxProvider[]>("/api/fx/providers?activeOnly=false"),
        loadCoBk(),
      ]);
      setList(ex);
      const pm: Record<number, string> = {};
      for (const p of pr) pm[p.id] = p.name;
      setProviders(pm);
      setCobk(cb);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  if (error) return <ErrorBox text={error} />;
  if (list === null) return <SkeletonRows />;
  if (list.length === 0)
    return (
      <div className="text-tg-hint text-sm py-8 text-center">
        Нет активных обменов, ожидающих возврата.
      </div>
    );
  return (
    <div className="space-y-2">
      {list.map((ex) => (
        <ExchangeRow
          key={ex.id}
          ex={ex}
          providerName={providers[ex.provider_id] || `#${ex.provider_id}`}
          cobk={cobk}
          onChanged={load}
        />
      ))}
    </div>
  );
}

function ExchangeRow({
  ex,
  providerName,
  cobk,
  onChanged,
}: {
  ex: FxExchange;
  providerName: string;
  cobk: CoBk | null;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [actual, setActual] = useState(String(ex.expected_return));
  const [tco, setTco] = useState<number | null>(ex.target_company_id);
  const [tbk, setTbk] = useState<number | null>(ex.target_bank_id);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const settle = async () => {
    const a = parseDec(actual);
    if (!(a >= 0)) {
      setError("Введите фактическую сумму возврата.");
      return;
    }
    if (tco == null || tbk == null) {
      setError("Укажите компанию и банк получения.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/fx/exchanges/${ex.id}/settle`, {
        actual_return: a,
        target_company_id: tco,
        target_bank_id: tbk,
        notes: notes.trim() || null,
      });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/fx/exchanges/${ex.id}/cancel`);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const a = parseDec(actual);
  const diff = a - ex.expected_return;
  const banks = tco != null && cobk ? cobk.banksByCompany[tco] || [] : [];
  const inputCls = "w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm";
  return (
    <div className="bg-tg-sectionBg rounded-xl p-3 text-sm">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-medium">
          Обмен #{ex.id} · {providerName}
        </span>
        <StatusPill tone="warning">ожидает возврата</StatusPill>
      </div>
      <div className="text-xs text-tg-hint mt-0.5">
        Отправлено {ex.amount_sent.toLocaleString("ru-RU")} {ex.from_currency} ·
        курс {Number(ex.declared_rate).toFixed(4)} → ожидаем{" "}
        {ex.expected_return.toLocaleString("ru-RU")} {ex.to_currency}
      </div>
      {!open ? (
        <div className="flex gap-4 mt-2">
          <button onClick={() => setOpen(true)} className="text-tg-link text-xs">
            Зафиксировать возврат
          </button>
          <button
            onClick={cancel}
            disabled={busy}
            className="text-tg-destructive text-xs disabled:opacity-50"
          >
            Отменить обмен
          </button>
        </div>
      ) : (
        <div className="mt-2 pt-2 border-t border-tg-secondaryBg space-y-2">
          <label className="block text-xs text-tg-hint">
            Фактически получено, {ex.to_currency}
            <input
              value={actual}
              onChange={(e) => setActual(e.target.value)}
              inputMode="decimal"
              className={"mt-0.5 " + inputCls}
            />
          </label>
          {Math.abs(diff) > 1e-6 && (
            <div
              className={
                "text-xs " + (diff < 0 ? "text-red-700" : "text-amber-700")
              }
            >
              {diff < 0
                ? `Дебиторка провайдера: ${(-diff).toLocaleString("ru-RU")} ${ex.to_currency}`
                : `Излишек (кредиторка/погашение долга): ${diff.toLocaleString("ru-RU")} ${ex.to_currency}`}
            </div>
          )}
          <div className="flex gap-2">
            <label className="block text-xs text-tg-hint flex-1">
              Компания получения
              <select
                value={tco ?? ""}
                onChange={(e) => {
                  setTco(e.target.value ? parseInt(e.target.value, 10) : null);
                  setTbk(null);
                }}
                className={"mt-0.5 " + inputCls}
              >
                <option value="">—</option>
                {(cobk?.companies || []).map(([id, n]) => (
                  <option key={id} value={id}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-xs text-tg-hint flex-1">
              Банк получения
              <select
                value={tbk ?? ""}
                onChange={(e) =>
                  setTbk(e.target.value ? parseInt(e.target.value, 10) : null)
                }
                className={"mt-0.5 " + inputCls}
                disabled={tco == null}
              >
                <option value="">—</option>
                {banks.map(([id, n]) => (
                  <option key={id} value={id}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {diff > 1e-6 && (
            <label className="block text-xs text-tg-hint">
              Комментарий о причине излишка
              <input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={"mt-0.5 " + inputCls}
              />
            </label>
          )}
          {error && <div className="text-xs text-red-700">{error}</div>}
          <div className="flex gap-2">
            <button
              onClick={settle}
              disabled={busy}
              className="flex-1 px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
            >
              Зафиксировать
            </button>
            <button
              onClick={() => setOpen(false)}
              className="px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
            >
              Отмена
            </button>
          </div>
        </div>
      )}
      {!open && error && (
        <div className="text-xs text-red-700 mt-1">{error}</div>
      )}
    </div>
  );
}

function ProviderLedgerView() {
  const [providers, setProviders] = useState<FxProvider[]>([]);
  const [providerId, setProviderId] = useState<number | null>(null);
  const [entries, setEntries] = useState<FxProviderLedgerEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setProviders(
          await api.get<FxProvider[]>("/api/fx/providers?activeOnly=false"),
        );
        const me = await api.get<MeResponse>("/api/me");
        setIsAdmin(me.roles.includes("admin"));
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  const writeOff = async (entry: FxProviderLedgerEntry) => {
    const ok = await confirmAsync(
      "Списать дебиторку как невозвратную? Себестоимость лота и P&L " +
        "связанных заявок будут пересчитаны. Действие необратимо.",
    );
    if (!ok) return;
    try {
      await api.post(`/api/fx/ledger/${entry.id}/writeoff`, {});
      notifyHaptic("success");
      if (providerId != null) await loadLedger(providerId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const loadLedger = async (pid: number) => {
    setEntries(null);
    try {
      setEntries(
        await api.get<FxProviderLedgerEntry[]>(
          `/api/fx/providers/${pid}/ledger?onlyOpen=true`,
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const resolve = async (entry: FxProviderLedgerEntry) => {
    const note = window.prompt(
      "Ручное урегулирование кредиторки. Комментарий (причина/способ):",
      "",
    );
    if (note == null) return;
    try {
      await api.post(`/api/fx/ledger/${entry.id}/resolve_payable`, {
        action: "manual",
        notes: note,
      });
      if (providerId != null) await loadLedger(providerId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="space-y-3">
      {error && (
        <div className="p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">
          {error}
        </div>
      )}
      <select
        value={providerId ?? ""}
        onChange={(e) => {
          const v = e.target.value ? parseInt(e.target.value, 10) : null;
          setProviderId(v);
          if (v != null) loadLedger(v);
          else setEntries(null);
        }}
        className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
      >
        <option value="">— выберите провайдера —</option>
        {providers.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      {entries === null ? (
        providerId == null ? null : (
          <SkeletonRows />
        )
      ) : entries.length === 0 ? (
        <div className="text-tg-hint text-sm py-6 text-center">
          Открытых задолженностей нет.
        </div>
      ) : (
        <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
          {entries.map((e) => (
            <div key={e.id} className="px-4 py-3 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <span
                  className={
                    "font-medium " +
                    (e.kind === "receivable"
                      ? "text-red-700"
                      : "text-amber-700")
                  }
                >
                  {e.kind === "receivable"
                    ? "Дебиторка провайдера"
                    : "Кредиторка группы"}
                </span>
                <span className="tabular-nums font-semibold">
                  {e.remaining.toLocaleString("ru-RU")} {e.currency}
                </span>
              </div>
              <div className="text-xs text-tg-hint mt-0.5">
                Запись #{e.id}
                {e.exchange_id ? ` · обмен #${e.exchange_id}` : ""}
                {e.notes ? ` · ${e.notes}` : ""}
              </div>
              {e.kind === "payable" && (
                <button
                  onClick={() => resolve(e)}
                  className="text-tg-link text-xs mt-1"
                >
                  Урегулировать (вариант D)
                </button>
              )}
              {e.kind === "receivable" && isAdmin && (
                <button
                  onClick={() => writeOff(e)}
                  className="text-tg-destructive text-xs mt-1"
                >
                  Списать как невозвратную
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CryptoPoolView() {
  const [pool, setPool] = useState<FxPoolBalance[] | null>(null);
  const [cobk, setCobk] = useState<CoBk | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [currency, setCurrency] = useState("USDT");
  const [amount, setAmount] = useState("");
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [p, cb] = await Promise.all([
        api.get<FxPoolBalance[]>("/api/fx/crypto_pool"),
        loadCoBk(),
      ]);
      setPool(p);
      setCobk(cb);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const distribute = async () => {
    const a = parseDec(amount);
    if (!(a > 0) || companyId == null) {
      setError("Укажите сумму и компанию-плательщика.");
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      await api.post("/api/fx/crypto_distribute", {
        currency: currency.trim().toUpperCase(),
        amount: a,
        company_id: companyId,
      });
      setResult(`Распределено ${a.toLocaleString("ru-RU")} ${currency} на компанию.`);
      setAmount("");
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm";
  return (
    <div className="space-y-3">
      <div className="p-2.5 rounded-lg bg-tg-sectionBg text-xs text-tg-hint leading-relaxed">
        Крипто-пул группы — общий баланс по всем внутренним кошелькам.
        Распределение списывает крипту по FIFO и зачисляет лот по
        себестоимости на баланс компании-плательщика.
      </div>
      {error && (
        <div className="p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">
          {error}
        </div>
      )}
      {pool === null ? (
        <SkeletonRows />
      ) : pool.length === 0 ? (
        <div className="text-tg-hint text-sm py-4 text-center">
          Крипто-пул пуст.
        </div>
      ) : (
        <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
          {pool.map((b) => (
            <div
              key={b.currency}
              className="px-4 py-3 flex items-baseline justify-between"
            >
              <div className="text-xs text-tg-hint">
                средн. курс {b.average_rate.toFixed(4)} ₽/{b.currency}
              </div>
              <div className="font-semibold tabular-nums">
                {b.total_amount.toLocaleString("ru-RU", {
                  maximumFractionDigits: 4,
                })}{" "}
                <span className="text-tg-hint">{b.currency}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-tg-sectionBg rounded-xl p-3 space-y-2">
        <div className="text-xs font-medium">
          Распределить крипту на компанию-плательщика
        </div>
        <div className="flex gap-2">
          <input
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            placeholder="USDT"
            className={inputCls + " w-24"}
          />
          <input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            placeholder="Сумма"
            className={inputCls}
          />
        </div>
        <select
          value={companyId ?? ""}
          onChange={(e) =>
            setCompanyId(e.target.value ? parseInt(e.target.value, 10) : null)
          }
          className={inputCls}
        >
          <option value="">— компания-плательщик —</option>
          {(cobk?.companies || []).map(([id, n]) => (
            <option key={id} value={id}>
              {n}
            </option>
          ))}
        </select>
        {result && <div className="text-xs text-emerald-700">{result}</div>}
        <button
          onClick={distribute}
          disabled={busy}
          className="w-full px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
        >
          {busy ? "Распределяем…" : "Распределить"}
        </button>
      </div>
    </div>
  );
}

const WALLET_TYPE_RU: Record<string, string> = {
  group_pool: "Пул группы",
  company: "Компания",
  counterparty: "Контрагент",
};

function WalletsManage() {
  const [list, setList] = useState<FxWallet[] | null>(null);
  const [cobk, setCobk] = useState<CoBk | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [address, setAddress] = useState("");
  const [name, setName] = useState("");
  const [wtype, setWtype] = useState("group_pool");
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [w, cb] = await Promise.all([
        api.get<FxWallet[]>("/api/fx/wallets?activeOnly=true"),
        loadCoBk(),
      ]);
      setList(w);
      setCobk(cb);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post("/api/fx/wallets", {
        address,
        name,
        wallet_type: wtype,
        company_id: wtype === "company" ? companyId : null,
      });
      setAddress("");
      setName("");
      setWtype("group_pool");
      setCompanyId(null);
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };
  const deactivate = async (id: number) => {
    try {
      await api.del(`/api/fx/wallets/${id}`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const inputCls =
    "w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm";
  return (
    <div className="space-y-3">
      <div className="p-2.5 rounded-lg bg-tg-sectionBg text-xs text-tg-hint leading-relaxed">
        Реестр криптокошельков. По умолчанию кошелёк — в пуле группы;
        можно привязать к компании-плательщику. Кошельки контрагентов
        ведутся для адресной книги исходящих платежей.
      </div>
      {error && (
        <div className="p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">
          {error}
        </div>
      )}
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="w-full px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm"
        >
          ➕ Добавить кошелёк
        </button>
      )}
      {showForm && (
        <form onSubmit={submit} className="bg-tg-sectionBg rounded-xl p-3 space-y-2">
          <input
            placeholder="Адрес кошелька (TRC-20)"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className={inputCls}
          />
          <input
            placeholder="Имя / метка (опц.)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputCls}
          />
          <select
            value={wtype}
            onChange={(e) => setWtype(e.target.value)}
            className={inputCls}
          >
            <option value="group_pool">Пул группы</option>
            <option value="company">Компания-плательщик</option>
            <option value="counterparty">Контрагент (адресная книга)</option>
          </select>
          {wtype === "company" && (
            <select
              value={companyId ?? ""}
              onChange={(e) =>
                setCompanyId(e.target.value ? parseInt(e.target.value, 10) : null)
              }
              className={inputCls}
            >
              <option value="">— компания —</option>
              {(cobk?.companies || []).map(([id, n]) => (
                <option key={id} value={id}>
                  {n}
                </option>
              ))}
            </select>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving || !address.trim()}
              className="flex-1 px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
            >
              Сохранить
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
            >
              Отмена
            </button>
          </div>
        </form>
      )}
      {list === null ? (
        <SkeletonRows />
      ) : list.length === 0 ? (
        <div className="text-tg-hint text-sm py-6 text-center">
          Кошельки не добавлены.
        </div>
      ) : (
        <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
          {list.map((w) => (
            <div
              key={w.id}
              className="px-4 py-3 flex items-baseline justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="font-medium text-sm break-all">
                  {w.name || w.address}
                </div>
                <div className="text-xs text-tg-hint break-all">
                  {w.name ? w.address + " · " : ""}
                  {WALLET_TYPE_RU[w.wallet_type] || w.wallet_type}
                  {w.company_id != null && cobk
                    ? ` · ${cobk.coName[w.company_id] || "компания"}`
                    : ""}
                </div>
              </div>
              <button
                onClick={() => deactivate(w.id)}
                className="text-tg-destructive text-xs shrink-0"
              >
                Удалить
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const CRYPTO_CATS: Array<[string, string]> = [
  ["operator_card", "Требуют обработки"],
  ["dust", "Прочие (пыль)"],
  ["done", "Обработанные"],
];

function CryptoCatPill({ category }: { category: string }) {
  if (category === "done")
    return <StatusPill tone="success">обработана</StatusPill>;
  if (category === "dust")
    return <StatusPill tone="neutral">пыль</StatusPill>;
  return <StatusPill tone="warning">требует обработки</StatusPill>;
}

function CryptoTxView() {
  const [list, setList] = useState<FxCryptoTx[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cat, setCat] = useState("operator_card");

  const load = async () => {
    setList(null);
    setError(null);
    try {
      setList(
        await api.get<FxCryptoTx[]>(`/api/fx/crypto_txs?category=${cat}`),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {CRYPTO_CATS.map(([c, label]) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            className={
              "px-3 py-1.5 rounded-full text-xs " +
              (cat === c
                ? "bg-tg-button text-tg-buttonText"
                : "bg-tg-secondaryBg text-tg-hint")
            }
          >
            {label}
          </button>
        ))}
      </div>
      <div className="p-2.5 rounded-lg bg-tg-sectionBg text-xs text-tg-hint leading-relaxed">
        {cat === "dust"
          ? "Мелкие переводы с адресов-двойников, отфильтрованные как пыль. " +
            "Если перевод реальный — верните его в обработку."
          : cat === "done"
          ? "Обработанные крипто-транзакции."
          : "Крипто-транзакции по нашим кошелькам, требующие обработки — " +
            "укажите контрагента и назначение перевода."}
      </div>
      {error && <ErrorBox text={error} />}
      {list === null ? (
        <SkeletonRows />
      ) : list.length === 0 ? (
        <EmptyState text="Транзакций в этой категории нет." />
      ) : (
        <div className="space-y-2">
          {list.map((tx) => (
            <CryptoTxRow key={tx.id} tx={tx} onDone={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function CryptoTxRow({
  tx,
  onDone,
}: {
  tx: FxCryptoTx;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [cpId, setCpId] = useState<number | null>(tx.counterparty_id);
  const [comment, setComment] = useState(tx.comment);
  const [counterparties, setCounterparties] = useState<Counterparty[]>([]);
  const [templates, setTemplates] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testMode, setTestMode] = useState(false);
  const [candidates, setCandidates] = useState<FxCryptoTx[]>([]);
  const [mainTxid, setMainTxid] = useState("");

  const addr = tx.direction === "OUT" ? tx.to_addr : tx.from_addr;

  const openTest = async () => {
    setTestMode(true);
    setError(null);
    try {
      const all = await api.get<FxCryptoTx[]>("/api/fx/crypto_txs");
      const cand = all.filter(
        (x) =>
          x.txid !== tx.txid &&
          x.direction === tx.direction &&
          (x.direction === "OUT" ? x.to_addr : x.from_addr) === addr &&
          x.amount > tx.amount,
      );
      setCandidates(cand);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const markTest = async () => {
    if (!mainTxid) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/fx/crypto_txs/${tx.txid}/mark_test`, {
        main_txid: mainTxid,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const openCard = async () => {
    setOpen(true);
    try {
      const [cps, tpl] = await Promise.all([
        api.get<Counterparty[]>("/api/counterparties"),
        api.get<{ templates: string[] }>(
          `/api/fx/comment_templates?address=${encodeURIComponent(addr)}`,
        ),
      ]);
      setCounterparties(cps);
      setTemplates(tpl.templates);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/fx/crypto_txs/${tx.txid}/process`, {
        counterparty_id: cpId,
        comment: comment.trim(),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const dt = new Date(tx.timestamp_ms).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const inputCls = "w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm";
  return (
    <div className="bg-tg-sectionBg rounded-xl p-3 text-sm">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-medium">
          {tx.direction === "IN" ? "🟢 Входящий" : "🟠 Исходящий"} ·{" "}
          {tx.amount.toLocaleString("ru-RU")} {tx.currency}
        </span>
        <span className="text-xs text-tg-hint">{dt}</span>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-1">
        <CryptoCatPill category={tx.category} />
        {tx.poisoning_suspect && (
          <StatusPill tone="danger">address-poisoning</StatusPill>
        )}
      </div>
      {tx.poisoning_suspect && (
        <div className="text-xs text-red-700 mt-0.5">
          ⚠️ Возможный address-poisoning: адрес похож на известный
        </div>
      )}
      <div className="text-xs text-tg-hint mt-0.5 break-all">
        {tx.direction === "IN" ? "От: " : "Кому: "}
        {addr}
      </div>
      {tx.category === "dust" ? (
        <div className="mt-2">
          <button
            onClick={async () => {
              try {
                await api.post(`/api/fx/crypto_txs/${tx.txid}/restore`, {});
                onDone();
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              }
            }}
            className="text-tg-link text-xs"
          >
            Вернуть в обработку
          </button>
          {error && (
            <div className="text-xs text-red-700 mt-1">{error}</div>
          )}
        </div>
      ) : tx.category === "done" ? (
        <div className="text-xs text-tg-hint mt-2">
          ✅ Обработана{tx.comment ? ` · ${tx.comment}` : ""}
        </div>
      ) : !open && !testMode ? (
        <div className="flex gap-4 mt-2">
          <button onClick={openCard} className="text-tg-link text-xs">
            Обработать
          </button>
          <button onClick={openTest} className="text-tg-link text-xs">
            🧪 Это тестовый перевод
          </button>
        </div>
      ) : testMode ? (
        <div className="mt-2 pt-2 border-t border-tg-secondaryBg space-y-2">
          <div className="text-xs text-tg-hint">
            Привязать как тестовый к основному переводу на этот же адрес:
          </div>
          {candidates.length === 0 ? (
            <div className="text-xs text-tg-hint">
              Подходящих основных переводов не найдено.
            </div>
          ) : (
            <select
              value={mainTxid}
              onChange={(e) => setMainTxid(e.target.value)}
              className={inputCls}
            >
              <option value="">— выберите основной перевод —</option>
              {candidates.map((c) => (
                <option key={c.txid} value={c.txid}>
                  {c.amount.toLocaleString("ru-RU")} {c.currency} ·{" "}
                  {new Date(c.timestamp_ms).toLocaleString("ru-RU", {
                    day: "2-digit",
                    month: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </option>
              ))}
            </select>
          )}
          {error && <div className="text-xs text-red-700">{error}</div>}
          <div className="flex gap-2">
            <button
              onClick={markTest}
              disabled={busy || !mainTxid}
              className="flex-1 px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
            >
              Привязать
            </button>
            <button
              onClick={() => setTestMode(false)}
              className="px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
            >
              Отмена
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2 pt-2 border-t border-tg-secondaryBg space-y-2">
          <select
            value={cpId ?? ""}
            onChange={(e) =>
              setCpId(e.target.value ? parseInt(e.target.value, 10) : null)
            }
            className={inputCls}
          >
            <option value="">— контрагент (опц.) —</option>
            {counterparties.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {templates.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {templates.map((t, i) => (
                <button
                  key={i}
                  onClick={() => setComment(t)}
                  className="px-2 py-1 rounded-full bg-tg-secondaryBg text-xs text-tg-hint"
                >
                  {t.length > 32 ? t.slice(0, 32) + "…" : t}
                </button>
              ))}
            </div>
          )}
          <input
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Назначение перевода"
            className={inputCls}
          />
          {error && <div className="text-xs text-red-700">{error}</div>}
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={busy}
              className="flex-1 px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
            >
              Сохранить
            </button>
            <button
              onClick={() => setOpen(false)}
              className="px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
            >
              Отмена
            </button>
          </div>
        </div>
      )}
      {!open && error && (
        <div className="text-xs text-red-700 mt-1">{error}</div>
      )}
    </div>
  );
}

function TestTransferHelper() {
  const [wallets, setWallets] = useState<FxWallet[]>([]);
  const [walletId, setWalletId] = useState<number | null>(null);
  const [amount, setAmount] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setWallets(await api.get<FxWallet[]>("/api/fx/wallets?activeOnly=true"));
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  const wallet = wallets.find((w) => w.id === walletId) || null;
  const amt = parseDec(amount);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Не удалось скопировать — выделите адрес вручную.");
    }
  };

  const inputCls = "w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm";
  return (
    <div className="space-y-3">
      <div className="p-2.5 rounded-lg bg-tg-sectionBg text-xs text-tg-hint leading-relaxed">
        Помощник тестового перевода — необязательный шаг. Перед крупной
        отправкой можно отправить небольшой тест: выберите кошелёк и
        сумму, система покажет карточку с суммой и адресом для удобного
        копирования. Систему привязки теста к основному переводу можно
        не использовать — сработает авто-эвристика.
      </div>
      {error && (
        <div className="p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">
          {error}
        </div>
      )}
      <div className="bg-tg-sectionBg rounded-xl p-3 space-y-2">
        <Field label="Кошелёк-получатель">
          <select
            value={walletId ?? ""}
            onChange={(e) =>
              setWalletId(e.target.value ? parseInt(e.target.value, 10) : null)
            }
            className={inputCls}
          >
            <option value="">— выберите кошелёк —</option>
            {wallets.map((w) => (
              <option key={w.id} value={w.id}>
                {(w.name || w.address) + " · " +
                  (WALLET_TYPE_RU[w.wallet_type] || w.wallet_type)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Сумма тестового перевода, USDT">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setAmount("10")}
              className="flex-1 px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
            >
              10 USDT
            </button>
            <button
              type="button"
              onClick={() => setAmount("100")}
              className="flex-1 px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
            >
              100 USDT
            </button>
          </div>
        </Field>
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          placeholder="или своя сумма"
          className={inputCls}
        />
      </div>

      {wallet && amt > 0 && (
        <div className="bg-tg-sectionBg rounded-xl p-4 space-y-3">
          <div className="text-xs uppercase tracking-wider text-tg-hint">
            Карточка тестового перевода
          </div>
          <div>
            <div className="text-xs text-tg-hint">Сумма к переводу</div>
            <div className="text-2xl font-bold tabular-nums">
              {amt.toLocaleString("ru-RU")} USDT
            </div>
          </div>
          <div>
            <div className="text-xs text-tg-hint">
              Адрес кошелька ({wallet.network})
            </div>
            <div className="font-mono text-sm break-all bg-tg-secondaryBg rounded-lg p-2 mt-0.5">
              {wallet.address}
            </div>
          </div>
          <button
            onClick={() => copy(wallet.address)}
            className="w-full px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm"
          >
            {copied ? "✓ Адрес скопирован" : "📋 Копировать адрес"}
          </button>
          <div className="text-xs text-tg-hint">
            После теста основной перевод на этот же адрес система
            привяжет к тесту автоматически (окно 2 ч).
          </div>
        </div>
      )}
    </div>
  );
}

function AddressBook() {
  const [wallets, setWallets] = useState<FxWallet[] | null>(null);
  const [cpNames, setCpNames] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [w, cps] = await Promise.all([
          api.get<FxWallet[]>("/api/fx/wallets?walletType=counterparty"),
          api.get<Counterparty[]>("/api/counterparties"),
        ]);
        setWallets(w);
        const m: Record<number, string> = {};
        for (const c of cps) m[c.id] = c.name;
        setCpNames(m);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  const copy = async (addr: string) => {
    try {
      await navigator.clipboard.writeText(addr);
      setCopied(addr);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setError("Не удалось скопировать — выделите адрес вручную.");
    }
  };

  const f = filter.trim().toLowerCase();
  const shown = (wallets || []).filter(
    (w) =>
      !f ||
      (w.name || "").toLowerCase().includes(f) ||
      w.address.toLowerCase().includes(f) ||
      (w.counterparty_id != null &&
        (cpNames[w.counterparty_id] || "").toLowerCase().includes(f)),
  );
  // Группировка по контрагенту.
  const groups = new Map<string, FxWallet[]>();
  for (const w of shown) {
    const key =
      w.counterparty_id != null
        ? cpNames[w.counterparty_id] || `Контрагент #${w.counterparty_id}`
        : "Без привязки к контрагенту";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(w);
  }

  return (
    <div className="space-y-3">
      <div className="p-2.5 rounded-lg bg-tg-sectionBg text-xs text-tg-hint leading-relaxed">
        Проверенные адреса кошельков контрагентов. При исходящем платеже
        копируйте адрес <b>отсюда</b>, а не из переписки — так исключается
        отправка на адрес-двойник (address-poisoning). Добавление и
        привязка кошельков — во вкладке «Кошельки».
      </div>
      {error && <ErrorBox text={error} />}
      {wallets !== null && wallets.length > 8 && (
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="🔍 Поиск по контрагенту или адресу…"
          className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
        />
      )}
      {wallets === null ? (
        <SkeletonRows />
      ) : groups.size === 0 ? (
        <EmptyState
          icon="📇"
          text="Кошельки контрагентов не добавлены."
        />
      ) : (
        <div className="space-y-3">
          {[...groups.entries()].map(([cp, list]) => (
            <div
              key={cp}
              className="bg-tg-sectionBg rounded-xl overflow-hidden"
            >
              <div className="px-4 py-2 bg-tg-secondaryBg text-xs font-medium">
                {cp}
              </div>
              <div className="divide-y divide-tg-secondaryBg">
                {list.map((w) => (
                  <div key={w.id} className="px-4 py-2.5 text-sm">
                    {w.name && (
                      <div className="font-medium">{w.name}</div>
                    )}
                    <div className="font-mono text-xs break-all text-tg-hint">
                      {w.address}
                    </div>
                    <div className="text-[11px] text-tg-hint mt-0.5">
                      {w.network}
                    </div>
                    <button
                      onClick={() => copy(w.address)}
                      className="text-tg-link text-xs mt-1"
                    >
                      {copied === w.address
                        ? "✓ Скопировано"
                        : "📋 Копировать адрес"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===================== P&L =====================

function PnLView() {
  const [cp, setCp] = useState("");
  const [company, setCompany] = useState("");
  const [bank, setBank] = useState("");
  const [amount, setAmount] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [report, setReport] = useState<FxPnLReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (cp.trim()) qs.set("counterparty", cp.trim());
      if (company.trim()) qs.set("company", company.trim());
      if (bank.trim()) qs.set("bank", bank.trim());
      if (amount.trim()) qs.set("amount", String(parseDec(amount)));
      if (dateFrom) qs.set("date_from", dateFrom);
      if (dateTo) qs.set("date_to", dateTo);
      const r = await api.get<FxPnLReport>(
        `/api/fx/pnl${qs.toString() ? `?${qs.toString()}` : ""}`,
      );
      setReport(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm";

  return (
    <div className="space-y-3">
      <div className="p-2.5 rounded-lg bg-tg-sectionBg text-xs text-tg-hint leading-relaxed">
        В P&L попадают только заявки с подтверждённой бухгалтером
        оплатой. После фактической оплаты результат пересчитывается
        автоматически.
      </div>

      <form
        onSubmit={load}
        className="bg-tg-sectionBg rounded-xl p-3 space-y-2"
      >
        <input
          value={cp}
          onChange={(e) => setCp(e.target.value)}
          placeholder="Контрагент"
          className={inputCls}
        />
        <div className="flex gap-2">
          <input
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="Компания"
            className={inputCls}
          />
          <input
            value={bank}
            onChange={(e) => setBank(e.target.value)}
            placeholder="Банк"
            className={inputCls}
          />
        </div>
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Сумма заявки"
          inputMode="decimal"
          className={inputCls}
        />
        <div className="flex gap-2 items-center">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className={inputCls}
          />
          <span className="text-tg-hint text-xs">—</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className={inputCls}
          />
        </div>
        <div className="text-[11px] text-tg-hint">
          Период — по дате проведения оплаты
        </div>
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
          >
            {loading ? "…" : "Показать P&L"}
          </button>
          <button
            type="button"
            onClick={() =>
              api
                .download(
                  "fx_pnl_search",
                  {
                    counterparty: cp.trim() || undefined,
                    company: company.trim() || undefined,
                    bank: bank.trim() || undefined,
                    amount: amount.trim() ? parseDec(amount) : undefined,
                    date_from: dateFrom || undefined,
                    date_to: dateTo || undefined,
                  },
                  "fx_pnl.xlsx",
                )
                .catch((e) =>
                  setError(e instanceof Error ? e.message : String(e)),
                )
            }
            className="px-3 py-2 rounded-lg bg-tg-secondaryBg text-sm"
          >
            📥 XLSX
          </button>
        </div>
      </form>

      {error && (
        <div className="p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">
          {error}
        </div>
      )}

      {report && <PnLReportResult report={report} />}
    </div>
  );
}

function PnLValue({
  rub,
  pct,
  big,
}: {
  rub: number;
  pct: number | null;
  big?: boolean;
}) {
  const cls = rub >= 0 ? "text-emerald-700" : "text-red-700";
  return (
    <span
      className={`tabular-nums ${cls} ${
        big ? "font-bold text-lg" : "font-semibold"
      }`}
    >
      {rub.toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ₽
      {pct != null && (
        <span className={big ? "text-sm ml-1" : "text-xs ml-1"}>
          ({pct >= 0 ? "+" : ""}
          {pct.toFixed(4)} %)
        </span>
      )}
    </span>
  );
}

function PnLLine({ label, v }: { label: string; v: number }) {
  return (
    <div className="flex justify-between">
      <span className="text-tg-hint">{label}</span>
      <span className="tabular-nums">
        {v.toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ₽
      </span>
    </div>
  );
}

function PnLReportResult({ report }: { report: FxPnLReport }) {
  if (report.deal_count === 0) {
    return (
      <div className="text-tg-hint text-sm py-6 text-center">
        Заявок с подтверждённой оплатой по фильтру не найдено.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="bg-tg-sectionBg rounded-xl p-4 space-y-1.5 text-sm">
        <div className="text-xs text-tg-hint">
          Итого по {report.deal_count} заявкам
        </div>
        <PnLLine label="Ожидаемый приход RUB" v={report.total_expected} />
        <PnLLine label="Себестоимость FX" v={report.total_cost} />
        <PnLLine label="Агентские комиссии" v={report.total_commissions} />
        <div className="border-t border-tg-secondaryBg pt-2 flex justify-between items-baseline">
          <span className="font-semibold">P&L</span>
          <PnLValue rub={report.total_pnl} pct={report.total_pnl_pct} big />
        </div>
      </div>

      {report.groups.map((g, gi) => (
        <div key={gi} className="bg-tg-sectionBg rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-tg-secondaryBg text-xs font-medium">
            {g.company_name || "— компания —"}
            {g.bank_name ? ` · ${g.bank_name}` : ""}
          </div>
          <div className="divide-y divide-tg-secondaryBg">
            {g.items.map((it) => (
              <div key={it.deal_id} className="px-4 py-2.5 text-sm">
                <div className="flex justify-between items-baseline gap-3">
                  <span className="font-medium">Заявка №{it.deal_id}</span>
                  <PnLValue rub={it.rub_pnl} pct={it.pnl_pct} />
                </div>
                <div className="text-xs text-tg-hint mt-0.5">
                  {it.counterparty_name || "контрагент —"} ·{" "}
                  {it.amount.toLocaleString("ru-RU")} {it.currency}
                </div>
              </div>
            ))}
          </div>
          <div className="px-4 py-2 border-t border-tg-secondaryBg flex justify-between items-baseline text-sm">
            <span className="text-tg-hint">
              Подытог: {g.items.length} заявок
            </span>
            <PnLValue rub={g.subtotal_pnl} pct={g.subtotal_pnl_pct} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ===================== Report =====================

function ReportView() {
  const [period, setPeriod] = useState("this_month");
  const [customStart, setCustomStart] = useState<string | null>(null);
  const [customEnd, setCustomEnd] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setDownloading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = { period_key: period };
      if (period === "custom" && customStart && customEnd) {
        params.custom = [customStart, customEnd];
      }
      await api.download("fx_pnl_report", params, "fx_pnl_report.xlsx");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="bg-tg-sectionBg rounded-xl p-4 space-y-3">
        <div className="text-sm font-medium">
          📥 Управленческий отчёт за период
        </div>
        <div className="text-xs text-tg-hint">
          Xlsx со списком закрытых сделок периода: контрагент, компания,
          банк, сумма, ожидаемый приход RUB, списано FX, себестоимость,
          агентские комиссии и итоговый P&L. Внизу — агрегаты.
        </div>
        <PeriodPicker
          value={period}
          onChange={setPeriod}
          customStart={customStart}
          customEnd={customEnd}
          onCustomChange={(s, e) => {
            setCustomStart(s);
            setCustomEnd(e);
          }}
        />
        {error && (
          <div className="p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">
            {error}
          </div>
        )}
        <button
          onClick={run}
          disabled={downloading}
          className="w-full px-3 py-2.5 rounded-lg bg-tg-button text-tg-buttonText font-medium disabled:opacity-50"
        >
          {downloading ? "Готовим…" : "📥 Скачать xlsx"}
        </button>
      </div>
    </div>
  );
}
